import Phaser from 'phaser';
import {
  BOARD_COLS,
  BOARD_ROWS,
  CONVEYOR_SPEED,
  CONVEYOR_PADDING,
  CONVEYOR_MAX_ATTACKERS,
  DEFAULT_NUM_COLORS,
  DEFAULT_STACK_DEPTH,
  FONT_FAMILY,
  GAME_WIDTH,
  getGameHeight,
  getQueueY,
  CONVEYOR_ATTACKER_SCALE,
  DEFAULT_NUM_QUEUES,
  getQueueLaneWidth,
  QUEUE_LANE_GAP,
  getTotalLanesWidth,
  getLanesStartX,
  MAX_STACK_SLOTS,
} from '../config/constants';
import { BoardGeometry, computeBoardGeometry } from '../config/BoardGeometry';
import { GAME_COLORS, setActiveGameplayPalette, getGameplayColorHex, getGameplayColorName } from '../config/colors';
import { regenerateColorTextures } from '../graphics/generateSprites';
import { BoardSystem } from '../systems/BoardSystem';
import { ConveyorSystem } from '../systems/ConveyorSystem';
import { QueueSystem } from '../systems/QueueSystem';
import { StackSystem } from '../systems/StackSystem';
import { ShootingSystem, type ShotEvent, type FiringLine } from '../systems/ShootingSystem';
import type { LockedDoor } from '../types/board';
import { firingLineKey } from '../systems/firingLineKey';
import { LevelGenerator, type LevelConfig, getTierForLevel, generateRawQueue } from '../systems/LevelGenerator';
import { resolveLevelDifficulty, resolveLevelSeed } from '../levels/levelQueueParams';
import { createSeededRng } from '../utils/seededRandom';
import { LEVEL_METADATA, LEVEL_COUNT } from '../levels/imageLevels';
import type { ImageLevel } from '../levels/types';
import { padAndRemapLevel, padGrid } from '../levels/gridUtils';
import { getEffectiveP, getWinningSeed, DEFAULT_LEVEL_SEED } from '../levels/types';
import { createAttacker, createMultiColorAttacker, createHammer, type AttackerData, attackerSize, primaryColor, isExhausted as attackerExhausted } from '../entities/Attacker';

import { ProjectileSprite } from '../entities/Projectile';
import { HUD } from '../ui/HUD';
import { StackUI } from '../ui/StackUI';
import { PauseOverlay } from '../ui/PauseOverlay';
import { InfoOverlay, type LevelInfoData } from '../ui/InfoOverlay';
import { crispText } from '../ui/textUtils';
import { soundManager } from '../audio/SoundManager';
import { isDeadlocked, findStuckOnFrozenFrontLane } from '../systems/DeadlockDetector';
import { SoftDeadlockChecker } from '../systems/SoftDeadlockDetector';
import { launchGameScene, type GameSceneLaunchData } from '../types/GameSceneLaunchData';
import { BoardRenderer } from '../rendering/BoardRenderer';
import { setReducedParticles, setFxDisabled, fxDisabled } from '../config/particleConfig';
import { ConveyorRenderer } from '../rendering/ConveyorRenderer';
import { AttackerRenderer, counterRotateLabels } from '../rendering/AttackerRenderer';
import { QueueRenderer } from '../rendering/QueueRenderer';
import { HammerAnimator } from '../rendering/HammerAnimator';
import { PeekManager } from '../ui/PeekManager';
import { AutoplaySystem } from '../systems/AutoplaySystem';
import { formatTraceText } from '../systems/BotStrategy';
import { fadeToScene } from '../ui/sceneTransition';
import { ReachHUD } from '../ui/ReachHUD';
import {
  buildSolverIdMap,
  classifyWitness,
  computeWitnessArrivalSchedule,
  fetchWitness,
  formatDivergence,
  LEGACY_WITNESS_MESSAGE,
  REPLAY_LOG_PREFIX,
  WitnessDriver,
  witnessHasAdvanceActions,
  type ReplayView,
  type WitnessPacingOptions,
} from '../systems/WitnessReplaySystem';
import {
  buildFlowState,
  OrderedFlowEngine,
  type ArrivalRecord,
  type DispatchOutcome,
  type LapShotRecord,
} from '../systems/OrderedFlowEngine';

/** Who asked for a dispatch — recorded in the decision log. */
type DispatchActor = 'PLAYER' | 'AUTO' | 'REPLAY';

/**
 * Belt-time grace before a paused (entrance-animating) belt entry is force
 * resumed. The longest legitimate entrance is the 5-orb stagger at ~0.68s;
 * anything paused this long has a dead tween and will never resume itself.
 */
const ENTRANCE_STALL_SECONDS = 2.5;

/**
 * A lap whose OUTCOME is already decided and whose animation is still playing.
 *
 * `shots` came out of the shared simulator when the dispatch resolved; the belt
 * replays them in order as the attacker sweeps each firing line. Cubes are
 * logically dead from resolve time, so a later dispatch can never target one
 * whose kill animation has not caught up yet.
 */
interface PendingLap {
  attacker: AttackerData;
  shots: readonly LapShotRecord[];
  /** Index of the next unplayed shot. */
  next: number;
  outcome: DispatchOutcome;
}

/** What a dispatch entry point hands back to its caller. */
interface DispatchResult {
  attacker: AttackerData;
  outcome: DispatchOutcome;
}

export class GameScene extends Phaser.Scene {
  private level = 1;
  /** Display name for the current level. */
  private levelName?: string;
  /** Current image level data (for info overlay). */
  private currentImageLevel?: import('../levels/types').ImageLevel;

  // Board geometry (per-level, computed from grid dimensions)
  private boardGeometry!: BoardGeometry;

  // Systems
  private board!: BoardSystem;
  private conveyor!: ConveyorSystem;
  private queue!: QueueSystem;
  private stack!: StackSystem;
  private shooting!: ShootingSystem;

  // Active attackers on conveyor: attackerId -> AttackerData
  private activeAttackers = new Map<number, AttackerData>();

  // Board rendering (cubes, snakes, mega blocks, locked doors, blockade walls, barricades)
  private boardRenderer!: BoardRenderer;
  // Conveyor rendering (belt path, flow dots, flash, speed glow)
  private conveyorRenderer!: ConveyorRenderer;
  // Attacker rendering (sprites, transitions, fade animations)
  private attackerRenderer!: AttackerRenderer;
  // Queue rendering (lane display, frozen visuals, mystery reveal, shift animations)
  private queueRenderer!: QueueRenderer;
  // Hammer strike animation
  private hammerAnimator!: HammerAnimator;

  // UI
  private hud!: HUD;
  private stackUI!: StackUI;

  /**
   * The LOGICAL authority — see `docs/ordered-flow-semantics.md`.
   *
   * Every outcome in this scene is decided here, at the instant a dispatch
   * happens: the whole lap's kill list, the ammo spent, whether the attacker
   * survived, where it lands, and whether that landing loses the game. The
   * board, conveyor, queue and stack systems below are the PRESENTATION of that
   * decision — they are mutated as animations reach the right moment, never to
   * decide anything.
   */
  private flow!: OrderedFlowEngine;

  /** Laps whose outcome is fixed and whose animation is still playing. */
  private pendingLaps = new Map<number, PendingLap>();

  /** Cube count at the last quiescent model-vs-screen compare — debounces it. */
  private assertedAtCubeCount = -1;

  // Game state
  private gameEnded = false;


  // Win-state speed-up
  private winSpeedUpActive = false;
  private conveyorSpeedMultiplier = 1;
  private winCheckDirty = true;

  // Queue peek (delegated to PeekManager)
  private peekManager!: PeekManager;
  private reachHUD!: ReachHUD;

  // Pause
  private gamePaused = false;
  private pauseOverlay!: PauseOverlay;
  private ambientEmitter!: Phaser.GameObjects.Particles.ParticleEmitter;


  // Conveyor slot counter
  private conveyorCounterText!: Phaser.GameObjects.Text;
  private lastConveyorCounterStr = '';

  // Soft deadlock detection (winnability check with debounce)
  private softDeadlockChecker = new SoftDeadlockChecker(1000);

  // Guards an infinite loop if the same frozen attacker would be force-dispatched
  // twice in a row without any intervening state change. Defensive — shouldn't
  // happen in practice because a force-dispatch always removes the attacker
  // from the queue and puts it on the conveyor (non-empty conveyor breaks the
  // stuck condition), but we track it anyway.
  private lastForcedDispatchAttackerId: number | null = null;

  // Autoplay system
  private autoplaySystem?: AutoplaySystem;
  private autoplayEnabled = false;
  private autoplayTimer = 0;
  private autoplayToggleBtn?: Phaser.GameObjects.Container;
  private autoplayToggleBg?: Phaser.GameObjects.Graphics;
  private autoplayToggleLabel?: Phaser.GameObjects.Text;
  private autoplaySpeedMultiplier = 1;
  private autoplayTransitionStallTime = 0;
  private static readonly SPEED_STEPS = [1, 4, 10] as const;
  private speedStepIndex = 0; // index into SPEED_STEPS
  private speedBtn?: Phaser.GameObjects.Container;
  private speedBtnBg?: Phaser.GameObjects.Graphics;
  private speedBtnLabel?: Phaser.GameObjects.Text;
  private blendSliderContainer?: Phaser.GameObjects.Container;
  private fxToggleBtn?: Phaser.GameObjects.Container;
  private fxToggleBg?: Phaser.GameObjects.Graphics;
  private fxToggleLabel?: Phaser.GameObjects.Text;
  // Queue lane override (player-selectable, persists across retries)
  private queueOverride?: number;
  private queueSelectorBtn?: Phaser.GameObjects.Container;
  private queueSelectorBg?: Phaser.GameObjects.Graphics;
  private queueSelectorLabel?: Phaser.GameObjects.Text;

  // Decision log — records every dispatch (player + autoplay) for analysis
  private decisionLog: string[] = [];
  private decisionTick = 0;
  private logBtn?: Phaser.GameObjects.Container;

  // Witness replay — see systems/WitnessReplaySystem.ts. `explicit` is
  // `?replay=witness`; `autoplay` is Auto taking the proven line on a level
  // that ships a gen.proof.
  private witnessMode: 'off' | 'explicit' | 'autoplay' = 'off';
  private witnessDriver?: WitnessDriver;
  private witnessPending = false;
  private witnessStatusText?: Phaser.GameObjects.Text;
  private witnessReported = false;
  /**
   * GAME-TIME clock for the witness driver: wall delta scaled by the live
   * speed multiplier, accumulated every update. On this clock a lap and an
   * entrance cost the same at every speed step, which is what makes the
   * driver's pacing budgets meaningful (`ReplayView.gameTimeMs`).
   */
  private witnessGameTimeMs = 0;
  /** Attackers that have completed a lap into the stack — the runtime's `advance` counter. */
  private stackArrivals = 0;
  /** Live attacker ids in post-hammer-splice queue order, for the solver-id mapping. */
  private orderedAttackerIds: number[] = [];
  /** Attackers generated before hammers were spliced in — `initialState`'s id basis. */
  private rawQueueLength = 0;
  private levelHammerIndices?: number[];

  constructor() {
    super({ key: 'GameScene' });
  }

  /** Pre-loaded level data from launchGameScene (async-loaded during fade). */
  private preloadedLevel?: ImageLevel;

  init(data: GameSceneLaunchData): void {
    this.level = data.level ?? 1;
    this.queueOverride = data.queueOverride;
    this.preloadedLevel = data.levelData;
    this.levelName = undefined;
    this.activeAttackers.clear();
    this.gameEnded = false;
    this.pendingLaps.clear();
    this.winSpeedUpActive = false;
    setReducedParticles(false);
    this.conveyorSpeedMultiplier = 1;
    this.winCheckDirty = true; this.reachHUD?.markDirty();
    this.softDeadlockChecker.reset();
    this.lastForcedDispatchAttackerId = null;
    // PeekManager is reset here but created in create() after queue is available
    // (peekManager may not exist during the very first init() call)
    this.gamePaused = false;
    this.autoplayEnabled = false;
    this.autoplayTimer = 0;
    this.autoplayTransitionStallTime = 0;
    this.decisionLog = [];
    this.decisionTick = 0;
    this.autoplaySpeedMultiplier = 1;
    this.speedStepIndex = 0;
    this.witnessMode = 'off';
    this.witnessDriver = undefined;
    this.witnessPending = false;
    this.witnessReported = false;
    this.witnessGameTimeMs = 0;
    this.stackArrivals = 0;
    this.orderedAttackerIds = [];
    this.rawQueueLength = 0;
    this.levelHammerIndices = undefined;
    setFxDisabled(false);
  }

  create(): void {
    let levelData;
    let paddedVisualGrid: number[][] | undefined;

    // Use pre-loaded level data (loaded async during fade transition)
    const imageLevelIndex = this.level - 1;
    const imgLevel = this.preloadedLevel;

    // Prefer the proven `gen.config`, then the baked difficulty from the level
    // file, then positional computation. Shared with the audit (in-process AND
    // worker) and the solver so all of them generate the same queue for this
    // level.
    const difficulty = resolveLevelDifficulty(imgLevel, this.level);

    if (imgLevel) {
      this.levelName = imgLevel.name;
      this.currentImageLevel = imgLevel;

      // Queue-generation seed: the proof's `gen.seed`, else the level's baked
      // seed, else DEFAULT_LEVEL_SEED (42). Same accessor the audit worker and
      // the solver use, so all three agree on which queue this level is.
      const seed = resolveLevelSeed(imgLevel);

      const pr = padAndRemapLevel(imgLevel, BOARD_ROWS, BOARD_COLS);
      levelData = LevelGenerator.fromGrid({ imageGrid: pr.paddedGrid, seed, difficulty, snakeCols: pr.snakeCols, megaBlockDefs: pr.megaBlocks, lockedDoorDefs: pr.lockedDoors, blockadeWallDefs: pr.blockadeWalls, barricadeDefs: pr.barricades, hammerIndices: pr.hammerIndices });

      // Pad visual grid to match the padded gameplay grid dimensions
      if (imgLevel.visualGrid) {
        paddedVisualGrid = padGrid(imgLevel.visualGrid, BOARD_ROWS, BOARD_COLS);
      }

    } else {
      // Fall back to random generation when image levels exhausted ("infinite mode")
      this.currentImageLevel = undefined;
      // No level file here (infinite mode) — nothing can carry a baked seed.
      const seed = DEFAULT_LEVEL_SEED;
      const numColors = Math.min(DEFAULT_NUM_COLORS + Math.floor((this.level - 1) / 2), GAME_COLORS.length);
      const maxStackDepth = DEFAULT_STACK_DEPTH;
      const config: LevelConfig = {
        rows: BOARD_ROWS,
        cols: BOARD_COLS,
        numColors,
        maxStackDepth,
        seed,
      };
      levelData = LevelGenerator.generate(config, difficulty);
    }

    // ── Queue generation ──────────────────────────────────────────────────
    // Always use raw queue (Fisher-Yates shuffle, no easing)
    const rawQueue = generateRawQueue(
      levelData.grid,
      levelData.config.seed,
      difficulty,
      levelData.megaBlockDefs,
      levelData.blockadeWallDefs,
    );
    levelData.attackerQueue = rawQueue;
    console.log(`[Queue] Level ${this.level}: ${rawQueue.length} attackers`);

    // ── Background ───────────────────────────────────────────────────────
    BoardRenderer.drawBackground(this);

    // ── Auto-pick color for locked doors / blockade walls ──────────────
    // Use seeded RNG so color assignment is deterministic for the same seed.
    const sceneRng = createSeededRng(levelData.config.seed + 7777);

    const autoPickColors = (defs: { colorIndex?: number }[] | undefined) => {
      if (!defs) return;
      const gridColors = new Set<number>();
      for (const row of levelData.grid) {
        for (const stack of row) {
          for (const c of stack) {
            gridColors.add(c);
          }
        }
      }
      const colorArr = Array.from(gridColors);
      for (const def of defs) {
        if (def.colorIndex === undefined && colorArr.length > 0) {
          def.colorIndex = colorArr[sceneRng.randomInt(0, colorArr.length - 1)];
        }
      }
    };

    autoPickColors(levelData.lockedDoorDefs);
    autoPickColors(levelData.blockadeWallDefs);

    // ── Systems ──────────────────────────────────────────────────────────
    this.board = new BoardSystem({
      grid: levelData.grid,
      snakeCells: levelData.snakeCells,
      megaBlockDefs: levelData.megaBlockDefs,
      lockedDoorDefs: levelData.lockedDoorDefs,
      blockadeWallDefs: levelData.blockadeWallDefs,
      barricadeDefs: levelData.barricadeDefs,
    });

    // Compute per-level board geometry from the grid dimensions
    this.boardGeometry = computeBoardGeometry(levelData.grid);

    const trackLeft = this.boardGeometry.offsetX - CONVEYOR_PADDING;
    const trackTop = this.boardGeometry.offsetY - CONVEYOR_PADDING;
    const trackRight = this.boardGeometry.offsetX + this.boardGeometry.boardWidth + CONVEYOR_PADDING;
    const trackBottom = this.boardGeometry.offsetY + this.boardGeometry.boardHeight + CONVEYOR_PADDING;
    this.conveyor = ConveyorSystem.createRoundedRectPath(
      trackLeft, trackTop, trackRight, trackBottom, 20,
    );

    const attackers = levelData.attackerQueue.map(entry => {
      const a = entry.orbs
        ? createMultiColorAttacker(entry.orbs)
        : createAttacker(entry.colorIndex, entry.ammo, entry.size, entry.mystery ?? false, entry.frozen);
      // For multi-color attackers, frozen needs to be set separately since createMultiColorAttacker doesn't accept it
      if (entry.orbs && entry.frozen && entry.frozen > 0) {
        a.frozen = entry.frozen;
      }
      return a;
    });

    // Apply frozenIndices from image level data (if present)
    if (imgLevel) {
      if (imgLevel.frozenIndices) {
        for (const fi of imgLevel.frozenIndices) {
          if (fi.index >= 0 && fi.index < attackers.length) {
            attackers[fi.index].frozen = fi.counter;
          }
        }
      }
    }

    // Insert hammer items at specified queue positions
    const hammerIndices = levelData.hammerIndices ?? imgLevel?.hammerIndices;
    if (hammerIndices) {
      // Sort in descending order so inserting doesn't shift later indices
      const sorted = [...hammerIndices].sort((a, b) => b - a);
      for (const idx of sorted) {
        const clampedIdx = Math.min(idx, attackers.length);
        attackers.splice(clampedIdx, 0, createHammer());
      }
    }

    // Witness replay needs to line these up with `SolverState.initialState`'s
    // attacker ids — see buildSolverIdMap.
    this.rawQueueLength = rawQueue.length;
    this.levelHammerIndices = hammerIndices;
    this.orderedAttackerIds = attackers.map(a => a.id);

    const numQueues = this.queueOverride ?? this.currentImageLevel?.numQueues ?? DEFAULT_NUM_QUEUES;
    this.queue = new QueueSystem(attackers, numQueues);
    const stackSlots = this.currentImageLevel?.stackSlots ?? MAX_STACK_SLOTS;
    this.stack = new StackSystem(stackSlots);
    this.shooting = new ShootingSystem(this.board);

    // ── Logical model ────────────────────────────────────────────────────
    // Built from the objects we just built, NOT from a second reading of the
    // level file: `padGrid`, `fromGrid`'s single-colour safeguard and
    // `autoPickColors` all sit between the file and this board, and any of them
    // would silently shift a cell coordinate or a colour. See `buildFlowState`.
    this.pendingLaps.clear();
    // LIVE mode: the belt is real-time and a rider leaves it only at lap end,
    // so the model's age-bound forced arrival never fires here — evicting a
    // mid-lap rider is the mechanic Arthur rejected. Everything else (room-
    // gated dispatch, arrival = the loss, immediate win) is the shared model.
    this.flow = new OrderedFlowEngine(buildFlowState({
      grid: levelData.grid,
      snakeCells: levelData.snakeCells,
      megaBlockDefs: levelData.megaBlockDefs,
      lockedDoorDefs: levelData.lockedDoorDefs,
      blockadeWallDefs: levelData.blockadeWallDefs,
      barricadeDefs: levelData.barricadeDefs,
      attackers,
      numQueues,
      stackSlots,
      conveyorCap: CONVEYOR_MAX_ATTACKERS,
    }), { mode: 'live' });

    // ── Per-level gameplay palette ─────────────────────────────────────
    setActiveGameplayPalette(imgLevel?.gameplayPalette);
    if (imgLevel?.gameplayPalette) {
      regenerateColorTextures(this.textures, imgLevel.gameplayPalette);
    }

    // ── Board renderer ──────────────────────────────────────────────────
    this.boardRenderer = new BoardRenderer(this, this.board, this.boardGeometry, imgLevel?.visualPalette, paddedVisualGrid, imgLevel?.gameplayMap);

    // ── Conveyor renderer ────────────────────────────────────────────────
    this.conveyorRenderer = new ConveyorRenderer(this, this.conveyor);
    this.conveyorRenderer.drawConveyorPath();
    this.conveyorRenderer.createConveyorDots();

    // ── Attacker renderer ────────────────────────────────────────────────
    this.attackerRenderer = new AttackerRenderer(this, this.conveyor);

    // ── Queue renderer ───────────────────────────────────────────────────
    this.queueRenderer = new QueueRenderer(this, this.queue, this.attackerRenderer);

    // ── Hammer animator ──────────────────────────────────────────────────
    this.hammerAnimator = new HammerAnimator(this, this.board, this.boardRenderer, () => {
      this.queueRenderer.drawQueueDisplay();
    });

    // ── Board ────────────────────────────────────────────────────────────
    this.boardRenderer.drawBoardBackground();
    this.boardRenderer.drawBoard();
    this.boardRenderer.drawSnakeOverlays();
    this.boardRenderer.drawBlockadeWalls();
    this.boardRenderer.drawBarricades();

    // ── UI ────────────────────────────────────────────────────────────────
    this.hud = new HUD(this, () => this.togglePause(), () => this.showLevelInfo(), () => {
      fadeToScene(this, () => {
        this.scene.sleep('GameScene');
        this.scene.launch('MenuScene');
      });
    });
    this.stackUI = new StackUI(this, this.stack.getCapacity());
    this.queueRenderer.drawQueueDisplay();
    this.peekManager = new PeekManager(this, this.queue, () => this.gameEnded);
    this.peekManager.reset();
    this.peekManager.create();

    // ── Ambient particles ────────────────────────────────────────────────
    this.ambientEmitter = this.add.particles(GAME_WIDTH / 2, getGameHeight() / 2, 'particle', {
      x: { min: -GAME_WIDTH / 2, max: GAME_WIDTH / 2 },
      y: { min: -getGameHeight() / 2, max: getGameHeight() / 2 },
      speed: { min: 3, max: 12 },
      scale: { start: 0.3, end: 0 },
      alpha: { start: 0.15, end: 0 },
      lifespan: 4000,
      frequency: 250,
      tint: 0x6666aa,
      blendMode: Phaser.BlendModes.ADD,
    });
    this.ambientEmitter.setDepth(-5);

    // ── Input ────────────────────────────────────────────────────────────
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      soundManager.init(); // unlock audio on first user gesture
      this.handleTap(pointer.x, pointer.y);
    });

    // ── Pause overlay ──────────────────────────────────────────────────
    this.pauseOverlay = new PauseOverlay(this, () => {
      this.gamePaused = false;
      this.ambientEmitter.resume();
    });

    // Resume from sleep (when returning from MenuScene)
    this.events.on('wake', () => {
      this.gamePaused = false;
      this.ambientEmitter.resume();
      if (this.pauseOverlay.visible) {
        this.pauseOverlay.hide();
      }
    });

    // ── Autoplay system ────────────────────────────────────────────────
    this.autoplaySystem = new AutoplaySystem(
      this.board, this.conveyor, this.queue, this.stack,
      (id) => this.activeAttackers.get(id),
      750,
    );
    this.createAutoplayToggle();
    this.applyAutoplayUrlParams();
    this.createLogButton();
    this.createFxToggle();
    this.createQueueSelector();
    this.createBlendSlider();

    // Reachability HUD — positioned to the right of the board
    const reachHudX = this.boardGeometry.offsetX + this.boardGeometry.boardWidth + CONVEYOR_PADDING + 8;
    const reachHudY = this.boardGeometry.offsetY;
    this.reachHUD = new ReachHUD(this, this.board, reachHudX, reachHudY);

    // Conveyor slot counter — positioned over the bottom-left corner of the conveyor (spawn/despawn point)
    const counterX = this.boardGeometry.offsetX - CONVEYOR_PADDING;
    const counterY = this.boardGeometry.offsetY + this.boardGeometry.boardHeight + CONVEYOR_PADDING + 12;
    this.conveyorCounterText = crispText(this.add.text(counterX, counterY, `${this.conveyor.slotsUsed()}/${CONVEYOR_MAX_ATTACKERS}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: '#8888aa',
    })).setOrigin(0.5, 0).setDepth(100);

    // Update URL so refreshing returns to this level (use stable UUID if available)
    const levelMeta = (this.level >= 1 && this.level <= LEVEL_COUNT) ? LEVEL_METADATA[this.level - 1] : undefined;
    const levelUuid = levelMeta?.uuid;
    history.replaceState(null, '', '?level=' + (levelUuid ?? this.level));

    // Register shutdown cleanup for scene transitions
    this.events.on('shutdown', this.cleanup, this);
  }

  /** Clean up all tweens, timers, renderers, and event listeners on scene transition. */
  private cleanup(): void {
    // Kill all tweens (including infinite repeat: -1 tweens)
    this.tweens.killAll();

    // Clean up renderers
    this.boardRenderer?.destroy();
    this.conveyorRenderer?.destroy();
    this.attackerRenderer?.destroy();
    this.queueRenderer?.destroy();

    // Clean up PeekManager timer and overlay
    this.peekManager?.destroy();

    // Clean up reachability HUD
    this.reachHUD?.destroy();

    // Clean up autoplay worker and reset speed
    this.autoplaySystem?.destroy();
    this.witnessMode = 'off';
    this.witnessDriver = undefined;
    this.witnessPending = false;
    this.witnessStatusText = undefined;
    this.autoplayEnabled = false;
    this.autoplayTimer = 0;
    this.autoplayTransitionStallTime = 0;
    this.autoplaySpeedMultiplier = 1;
    this.speedStepIndex = 0;
    this.time.timeScale = 1;
    this.tweens.timeScale = 1;
    setFxDisabled(false);

    // Clear game state maps
    this.activeAttackers.clear();
    this.pendingLaps.clear();

    // Remove input listeners
    this.input.removeAllListeners();
  }

  update(time: number, delta: number): void {
    if (this.gameEnded) {
      // A loss (or an early win) ends the scene before the driver's own tick
      // can see it — report the verdict from here instead.
      this.reportWitnessReplay();
      return;
    }
    if (this.gamePaused) return;

    const rawDt = delta / 1000;
    // Apply autoplay speed multiplier to game simulation (but NOT to autoplay timer)
    const dt = rawDt * this.autoplaySpeedMultiplier;

    // The witness driver's game-time clock advances exactly as the belt does —
    // scaled by the speed multiplier — so its pacing budgets stay meaningful
    // at 1×/4×/10× (see ReplayView.gameTimeMs).
    this.witnessGameTimeMs += delta * this.autoplaySpeedMultiplier;

    // Apply win-state speed-up multiplier (ramped by tween when guaranteed win detected)
    if (this.winSpeedUpActive) {
      this.conveyor.setSpeed(CONVEYOR_SPEED * this.conveyorSpeedMultiplier);
      this.autoDispatch();
    }

    // Advance conveyor. An attacker crossing t >= 1.0 has finished its lap —
    // that is the live game's `settle` event (semantics doc §2).
    const completedEntries = this.conveyor.update(dt);
    for (const entry of completedEntries) {
      this.finishRider(entry.attackerId);
      if (this.gameEnded) return;
    }

    // Ring accounting is owned by the LOGIC; the belt is pure display. First
    // un-stick any entrance animation that was skipped or interrupted (its
    // resume callback will never fire), then settle any ring rider the belt is
    // no longer presenting — a rider with no belt entry can never finish a lap
    // on screen, and leaving it in the ring leaks its slots forever (the
    // fire-station soft-lock: belt renders 0/5, every dispatch refused).
    this.unstickStalledEntrances();
    this.reconcileFlowRing();
    if (this.gameEnded) return;

    // Update attacker sprite positions
    this.attackerRenderer.updateAttackerSpritePositions();

    // Advance conveyor flow dots
    this.conveyorRenderer.updateConveyorDots(dt);

    // Check for hammer at front of queue — auto-trigger if present. The model
    // consumes hammers inside `dispatch` (semantics doc §1 step 5) by the same
    // rule (lowest lane front, lowest-id live barricade), so this is the
    // animation of a strike the model has already made.
    if (!this.hammerAnimator.hammerAnimating) {
      const hammer = this.queue.checkForHammer();
      if (hammer) {
        const barricades = this.board.getActiveBarricades();
        if (barricades.length > 0) {
          this.hammerAnimator.animateHammerStrike(barricades[0]);
        }
      }
    }

    // Reveal mystery orbs at front of queue before dispatch/shooting
    this.queueRenderer.checkAndRevealFrontMysteryOrbs();

    // Play the shots the model already resolved, as the belt reaches their lines.
    this.advanceLapPresentation();
    if (this.gameEnded) return;

    // Check if win is guaranteed — speed up conveyor to finish fast
    if (!this.winSpeedUpActive) {
      this.checkGuaranteedWin();
    }

    // The model itself reached a dead end (no legal dispatch, nothing in flight,
    // board not clear). That is the authority; the heuristic checks below are
    // the presentation's own, kinder read of the same situation.
    if (this.flow.lost) {
      this.triggerDeadlockGameOver();
      return;
    }

    // Check for deadlock — no possible moves left. Only meaningful once every
    // resolved lap has finished animating, since until then the rendered board
    // and attacker ammo lag the model.
    if (!this.winSpeedUpActive && this.pendingLaps.size === 0 && this.checkForDeadlock()) {
      this.triggerDeadlockGameOver();
      return;
    }

    // Check for "stuck on frozen front": conveyor + stack empty, every queue
    // front frozen. No normal move is possible and frozen counters can't tick
    // (they only decrement when conveyor attackers exhaust). Auto-dispatch
    // the frozen front of the lowest-numbered lane to keep the game playable.
    // This mirrors StrategicSimulator's + PerfectSolver's force-dispatch
    // semantics so runtime winnability matches audit/solver results.
    if (!this.winSpeedUpActive && this.handleStuckOnFrozenFront()) {
      return;
    }

    // Check for soft deadlock — game is mathematically unwinnable. Same gate:
    // it reads the rendered board and the sprites' ammo, both of which trail the
    // model while a lap is still playing out.
    if (!this.winSpeedUpActive && this.pendingLaps.size === 0 && this.checkForSoftDeadlock()) {
      this.triggerSoftDeadlockGameOver();
      return;
    }

    this.assertPresentationMatchesModel();

    // ── Witness replay tick ──
    // Takes the place of the autoplay tick below (see witnessActive()), and
    // paces itself on game readiness rather than on a timer.
    if (this.witnessDriver) {
      this.tickWitnessReplay(time);
    }

    // ── Autoplay tick ──
    // Match StrategicSimulator exactly: dispatch ONE attacker, wait for the full
    // lap to complete (conveyor empties as attackers exhaust or move to stack),
    // Dispatch whenever conveyor has space and no transitions are in-flight.
    // One dispatch per tick — the game loop naturally fills slots over successive frames.
    if (this.autoplayEnabled && this.autoplaySystem && !this.winSpeedUpActive && !this.witnessActive()) {
      const hasSpace = this.conveyor.canAdd();
      const transitionsClear = this.attackerRenderer.transitioningAttackers.size === 0;

      if (hasSpace && !transitionsClear) {
        // Conveyor has space but transition animations are stuck
        this.autoplayTransitionStallTime += delta;
        if (this.autoplayTransitionStallTime > 2000) {
          // Force-cleanup stale transitions after 2 seconds of wall-clock time
          console.warn(`[Autoplay] Force-clearing ${this.attackerRenderer.transitioningAttackers.size} stale transitions after 2s stall`);
          for (const id of this.attackerRenderer.transitioningAttackers) {
            this.attackerRenderer.cleanupTransitionAnimation(id);
            this.conveyor.resumeAttacker(id);
          }
          this.attackerRenderer.transitioningAttackers.clear();
        }
      } else {
        this.autoplayTransitionStallTime = 0;
      }

      if (hasSpace && transitionsClear) {
        this.executeAutoplayTick();
      }
    }

    // Update UI (skip setText when values haven't changed to avoid texture rebuilds)
    this.hud.update(this.level, this.board.totalCubesRemaining(), this.levelName);
    this.stackUI.update(this.stack.getSlots());
    this.reachHUD.update();
    const counterStr = `${this.conveyor.slotsUsed()}/${CONVEYOR_MAX_ATTACKERS}`;
    if (counterStr !== this.lastConveyorCounterStr) {
      this.lastConveyorCounterStr = counterStr;
      this.conveyorCounterText.setText(counterStr);
    }

    // Win condition. The MODEL wins the moment the board reaches zero cubes; the
    // screen waits for the killing shot's animation to land, which is exactly
    // when every resolved shot has been played (once the logical board is empty
    // no later dispatch can produce a shot, so the last one played IS the
    // killing one). `board.isCleared()` is the presentation's own confirmation
    // and must agree — it is the assertion, not the trigger.
    if (this.flow.won && this.allShotsPlayed()) {
      this.triggerWin();
    }
  }

  /**
   * ASSERTION LAYER (semantics doc, "Assertion layer").
   *
   * At quiescence — belt empty, nothing animating — the rendered board must be
   * the model's board, cell for cell. Anything else means the presentation
   * invented or lost a kill, which is the exact class of bug this architecture
   * exists to make impossible. It has no fallback on purpose: it shouts, and we
   * find out.
   *
   * Runs only at quiescence, which on a real level is a handful of times per
   * playthrough, so the O(cells) compare costs nothing.
   */
  private assertPresentationMatchesModel(): void {
    if (this.gameEnded) return;
    if (this.pendingLaps.size > 0) return;
    if (this.conveyor.attackerCount() > 0) return;
    if (this.attackerRenderer.transitioningAttackers.size > 0) return;
    if (this.assertedAtCubeCount === this.flow.cubesRemaining()) return;
    this.assertedAtCubeCount = this.flow.cubesRemaining();

    const model = this.flow.state;
    const grid = this.board.getGrid();
    for (let r = 0; r < model.boardHeight; r++) {
      for (let c = 0; c < model.boardWidth; c++) {
        const modelCell = model.board[r * model.boardWidth + c];
        const stack = grid[r]?.[c] ?? [];
        const shownCell = stack.length > 0 ? stack[stack.length - 1] + 1 : 0;
        if (modelCell !== shownCell) {
          console.error(
            `[ordered-flow] board divergence at (${r},${c}): model=${modelCell} rendered=${shownCell} ` +
            `— the presentation is no longer a replay of the model`,
          );
          this.assertedAtCubeCount = -1; // report again next time something changes
          return;
        }
      }
    }
  }

  /** True when no resolved shot is still waiting for the belt to reach its line. */
  private allShotsPlayed(): boolean {
    for (const pending of this.pendingLaps.values()) {
      if (pending.next < pending.shots.length) return false;
    }
    return true;
  }

  private triggerWin(): void {
    if (this.gameEnded) return;
    this.gameEnded = true;
    if (!this.board.isCleared()) {
      console.error(
        '[ordered-flow] the model won but the rendered board still shows ' +
        `${this.board.totalCubesRemaining()} cubes — presentation drifted from the model`,
      );
    }
    // Extract trace text now (disables tracing) — download deferred to GameOverScene
    const traceText = this.extractTraceText();
    soundManager.levelWin();
    this.time.delayedCall(500, () => {
      this.scene.pause();
      this.scene.launch('GameOverScene', {
        won: true, level: this.level,
        decisionLog: this.decisionLog.length > 0 ? [...this.decisionLog] : undefined,
        levelName: this.levelName,
        traceText,
        queueOverride: this.queueOverride,
      });
    });
  }

  private togglePause(): void {
    if (this.gameEnded) return;
    if (this.gamePaused) {
      // Already paused — shouldn't happen from button, but handle gracefully
      return;
    }
    this.gamePaused = true;
    this.ambientEmitter.pause();
    this.pauseOverlay.show();
  }

  /** Show developer info overlay with current level metadata. */
  private showLevelInfo(): void {
    if (this.gameEnded) return;

    const imgLevel = this.currentImageLevel;
    const difficulty = resolveLevelDifficulty(imgLevel, this.level);
    const tier = getTierForLevel(this.level);

    // Compute grid dimensions and cell count from the source (unpadded) grid
    let gridRows = 0;
    let gridCols = 0;
    let cellCount = 0;
    const colorSet = new Set<number>();

    if (imgLevel) {
      gridRows = imgLevel.grid.length;
      gridCols = imgLevel.grid[0]?.length ?? 0;
      for (const row of imgLevel.grid) {
        for (const val of row) {
          if (val >= 0) {
            cellCount++;
            colorSet.add(val);
          }
        }
      }
    }

    const colorNames = Array.from(colorSet).sort((a, b) => a - b)
      .map(c => getGameplayColorName(c));

    const data: LevelInfoData = {
      level: this.level,
      name: this.levelName,
      tier,
      gridRows,
      gridCols,
      cellCount,
      colorCount: colorSet.size,
      colorNames,
      winningSeed: imgLevel ? getWinningSeed(imgLevel) : undefined,
      effectiveP: imgLevel ? getEffectiveP(imgLevel) : undefined,
      hasSnakeCols: !!(imgLevel?.snakeCols && imgLevel.snakeCols.length > 0),
      hasMegaBlocks: !!(imgLevel?.megaBlocks && imgLevel.megaBlocks.length > 0),
      hasLockedDoors: !!(imgLevel?.lockedDoors && imgLevel.lockedDoors.length > 0),
      hasBlockadeWalls: !!(imgLevel?.blockadeWalls && imgLevel.blockadeWalls.length > 0),
      hasBarricades: !!(imgLevel?.barricades && imgLevel.barricades.length > 0),
      hasHammers: !!(imgLevel?.hammerIndices && imgLevel.hammerIndices.length > 0),
      hasFrozenIndices: !!(imgLevel?.frozenIndices && imgLevel.frozenIndices.length > 0),
      attackerCount: this.queue.queueLength(),
      difficulty,
    };

    const overlay = new InfoOverlay(this, data);
    overlay.show();
  }




  /** Log a dispatch decision (player, autoplay, or witness replay) with full game state context. */
  private logDecision(actor: DispatchActor, source: 'queue' | 'stack', index: number, att: { orbs: { colorIndex: number; ammo: number }[] }, scoring?: import('../systems/AutoplaySystem').ScoringInfo): void {
    const tick = this.decisionTick++;
    const conv = this.conveyor.slotsUsed();
    const stack = this.stack.count();
    const qLen = this.queue.queueLength();
    const cubes = this.board.totalCubesRemaining();
    const colors = att.orbs.map(o => `${getGameplayColorName(o.colorIndex)}(${o.ammo})`).join('+');
    const line = `${String(tick).padStart(4)} | ${actor.padEnd(6)} | ${source.padEnd(5)} ${source === 'queue' ? `lane=${index}` : `slot=${index}`} | ${colors.padEnd(20)} | conv=${conv} stack=${stack} queue=${qLen} cubes=${cubes}`;
    this.decisionLog.push(line);
    // Append scoring breakdown for bot dispatches
    if (scoring) {
      const b = scoring.breakdown;
      const scoreLine = `     → score=${b.total.toFixed(1)} [cubes=${b.cubesCleared} depl=${b.depletionBonus.toFixed(1)} ammo=${b.lowAmmoBonus.toFixed(1)} demand=${b.colorDemandBonus.toFixed(1)} stack=${b.stackSourceBonus} overflow=${b.stackOverflowPenalty.toFixed(1)} lookahead=${b.queueLookaheadBonus.toFixed(1)} waste=${b.wasteRatioPenalty.toFixed(1)} conv=${b.conveyorCrowdingPenalty} slot=${b.slotPressureBonus.toFixed(1)} grind=${b.grindBonus.toFixed(1)} eff=${b.ammoEfficiencyPenalty.toFixed(1)} unblock=${b.unblockBonus.toFixed(1)}] (of ${scoring.candidateCount} candidates)`;
      this.decisionLog.push(scoreLine);
    }
  }

  /** Download the decision log as a text file. */
  private downloadDecisionLog(): void {
    if (this.decisionLog.length === 0) return;
    const header = `pxFlow Decision Log — Level ${this.level}: ${this.levelName ?? 'unknown'}\n` +
      `Tick | Actor  | Source       | Color(ammo)          | State\n` +
      `${'—'.repeat(90)}\n`;
    const text = header + this.decisionLog.join('\n') + '\n';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decision-log-lv${this.level}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Extract trace text from AutoplaySystem (disabling trace) without triggering
   * an immediate download. Returns undefined if no trace is active/non-empty.
   */
  private extractTraceText(): string | undefined {
    if (!this.autoplaySystem?.traceActive) return undefined;
    const t = this.autoplaySystem.disableTrace();
    if (!t || t.entries.length === 0) return undefined;
    return formatTraceText(t.entries);
  }

  // ── Tap handling ─────────────────────────────────────────────────────────

  private handleTap(x: number, y: number): void {
    if (this.gameEnded) return;
    if (this.gamePaused) return;

    const slotIndex = this.stackUI.getSlotAt(x, y);
    if (slotIndex !== null) {
      if (!this.stack.getSlots()[slotIndex]) return;
      const result = this.dispatchFromStackSlot(slotIndex, 'PLAYER');
      if (!result) {
        soundManager.error();
        return;
      }
      this.invalidateAutoplaySolution();
      return;
    }

    for (const hit of this.queueRenderer.queueLaneHitAreas) {
      if (x >= hit.x && x <= hit.x + hit.w && y >= hit.y && y <= hit.y + hit.h) {
        // Frozen front — refuse with feedback. (Frozen is a MODEL rule, not a
        // pacing one: a frozen attacker genuinely cannot be dispatched, except
        // through the stuck-on-frozen-front escape hatch handled in update().)
        if (this.queue.isFrontFrozen(hit.lane)) {
          soundManager.error();
          this.queueRenderer.shakeFrozenAttacker(hit.lane);
          return;
        }
        const result = this.dispatchFromQueueLane(hit.lane, 'PLAYER');
        if (!result) {
          // Empty lane is silent; a lane with a blocked (multi-orb not ready)
          // front gets the buzz.
          if (this.queue.getQueueLane(hit.lane).length > 0) soundManager.error();
          return;
        }
        this.invalidateAutoplaySolution();
        return;
      }
    }
  }

  /** A hand-played dispatch diverges from any pre-computed bot line. */
  private invalidateAutoplaySolution(): void {
    if (!this.autoplayEnabled || !this.autoplaySystem) return;
    this.autoplaySystem.resetReplay();
    this.autoplaySystem.requestFullSolution();
  }

  private launchAttackerOnConveyor(attacker: AttackerData, fromPos?: { x: number; y: number; scale?: number }, orbFromPositions?: { x: number; y: number }[]): void {
    // The model's room gate is the ONLY capacity authority — a dispatch that
    // reached here was accepted, so the belt entry is unconditional (force).
    // The physical belt can legitimately be fuller than the model's ring: the
    // ring drops a culled rider at resolve time, while the belt keeps it until
    // its kill animations land. Refusing the entry here (the pre-2026-09
    // behaviour) stranded the rider in the model's ring with no lap animation
    // to ever settle it — the fire-station soft-lock (empty belt, every
    // dispatch refused). Over-rendering for a moment is display, not capacity.
    if (!this.conveyor.canAdd(attackerSize(attacker))) {
      console.warn(
        `[ordered-flow] belt over-renders for #${attacker.id} ` +
        `(${this.conveyor.slotsUsed()}/${CONVEYOR_MAX_ATTACKERS} shown) while culled riders finish animating`,
      );
    }
    this.conveyor.addAttacker(attacker.id, attackerSize(attacker), true, true);

    this.activeAttackers.set(attacker.id, attacker);

    const _ep = this.conveyor.getPositionAtT(0);
    const entryPos = { x: _ep.x, y: _ep.y };
    const entryAngle = this.conveyor.getAngleAtT(0);

    // ── Multi-orb staggered dispatch animation ──────────────────────────
    if (attacker.orbs.length > 1 && orbFromPositions && orbFromPositions.length === attacker.orbs.length && fromPos) {
      this.attackerRenderer.transitioningAttackers.add(attacker.id);

      const orbSprites: Phaser.GameObjects.Container[] = [];
      const connector = this.add.graphics();
      const STAGGER_DELAY = 100; // ms between each orb launch
      const ORB_TWEEN_DURATION = 280;
      const connectorThickness = 6;
      const connectorColor = 0x555577;
      const connectorAlpha = 0.7;
      let arrivedCount = 0;

      // Create individual orb visuals at their queue positions
      for (let i = 0; i < attacker.orbs.length; i++) {
        const orb = attacker.orbs[i];
        const startPos = orbFromPositions[i];
        const orbVis = this.attackerRenderer.createAttackerVisual(startPos.x, startPos.y, [orb]);
        orbVis.setScale(fromPos.scale ?? 1.1);
        orbSprites.push(orbVis);
      }

      // Per-frame connector redraw (same pattern as animateQueueShift)
      const redrawConnector = () => {
        connector.clear();
        connector.lineStyle(connectorThickness, connectorColor, connectorAlpha);
        for (let i = 0; i < orbSprites.length - 1; i++) {
          const p1 = orbSprites[i];
          const p2 = orbSprites[i + 1];
          connector.beginPath();
          connector.moveTo(p1.x, p1.y);
          connector.lineTo(p2.x, p2.y);
          connector.strokePath();
        }
      };

      // Draw initial connector
      redrawConnector();

      // Register per-frame update
      const updateHandler = () => { redrawConnector(); };
      this.events.on('update', updateHandler);

      // Track tweens and delayed calls for force-cleanup if attacker exhausts mid-animation
      const cleanupData: {
        orbSprites: Phaser.GameObjects.Container[];
        connector: Phaser.GameObjects.Graphics;
        updateHandler: () => void;
        tweens: Phaser.Tweens.Tween[];
        delayedCalls: Phaser.Time.TimerEvent[];
      } = { orbSprites, connector, updateHandler, tweens: [], delayedCalls: [] };
      this.attackerRenderer.transitionCleanup.set(attacker.id, cleanupData);

      // Stagger-tween each orb to the conveyor entry point
      for (let i = 0; i < orbSprites.length; i++) {
        const delayedCall = this.time.delayedCall(i * STAGGER_DELAY, () => {
          const tw = this.tweens.add({
            targets: orbSprites[i],
            x: entryPos.x,
            y: entryPos.y,
            scaleX: CONVEYOR_ATTACKER_SCALE,
            scaleY: CONVEYOR_ATTACKER_SCALE,
            duration: ORB_TWEEN_DURATION,
            ease: 'Back.easeOut',
            onComplete: () => {
              arrivedCount++;
              // All orbs arrived — clean up temp visuals, create real conveyor sprite
              if (arrivedCount === orbSprites.length) {
                this.events.off('update', updateHandler);
                connector.destroy();
                for (const s of orbSprites) s.destroy();
                this.attackerRenderer.transitionCleanup.delete(attacker.id);

                // Create the unified conveyor attacker visual
                const container = this.attackerRenderer.createAttackerVisual(entryPos.x, entryPos.y, attacker.orbs);
                container.setScale(CONVEYOR_ATTACKER_SCALE);
                container.setRotation(entryAngle);
                counterRotateLabels(container, entryAngle);
                this.attackerRenderer.attackerSprites.set(attacker.id, container);
                this.attackerRenderer.transitioningAttackers.delete(attacker.id);
                this.conveyor.resumeAttacker(attacker.id);
              }
            },
          });
          cleanupData.tweens.push(tw);
        });
        cleanupData.delayedCalls.push(delayedCall);
      }

      return;
    }

    // ── Single-orb or no per-orb positions: existing behavior ───────────
    if (fromPos) {
      // Smooth position lerp from source to conveyor entry
      const container = this.attackerRenderer.createAttackerVisual(fromPos.x, fromPos.y, attacker.orbs);
      container.setScale(fromPos.scale ?? 1);
      this.attackerRenderer.attackerSprites.set(attacker.id, container);
      this.attackerRenderer.transitioningAttackers.add(attacker.id);

      this.tweens.add({
        targets: container,
        x: entryPos.x,
        y: entryPos.y,
        scaleX: CONVEYOR_ATTACKER_SCALE,
        scaleY: CONVEYOR_ATTACKER_SCALE,
        rotation: entryAngle,
        duration: 200,
        ease: 'Power2',
        onUpdate: () => {
          // Keep text labels upright during transition tween
          counterRotateLabels(container, container.rotation);
        },
        onComplete: () => {
          this.attackerRenderer.transitioningAttackers.delete(attacker.id);
          this.conveyor.resumeAttacker(attacker.id);
        },
      });
    } else {
      // No source position — spring bounce in at conveyor entry
      const container = this.attackerRenderer.createAttackerVisual(entryPos.x, entryPos.y, attacker.orbs);
      container.setRotation(entryAngle);
      counterRotateLabels(container, entryAngle);
      this.attackerRenderer.attackerSprites.set(attacker.id, container);

      container.setScale(0);
      this.tweens.add({
        targets: container,
        scaleX: CONVEYOR_ATTACKER_SCALE,
        scaleY: CONVEYOR_ATTACKER_SCALE,
        duration: 300,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.conveyor.resumeAttacker(attacker.id);
        },
      });
    }
  }

  // ── Shooting ─────────────────────────────────────────────────────────────

  private getFiringLine(px: number, py: number): FiringLine | null {
    const { offsetX, offsetY, boardWidth, boardHeight, cellSize, rows, cols } = this.boardGeometry;
    const boardLeft = offsetX;
    const boardTop = offsetY;
    const boardRight = offsetX + boardWidth;
    const boardBottom = offsetY + boardHeight;

    const distLeft = Math.abs(px - (boardLeft - CONVEYOR_PADDING));
    const distRight = Math.abs(px - (boardRight + CONVEYOR_PADDING));
    const distTop = Math.abs(py - (boardTop - CONVEYOR_PADDING));
    const distBottom = Math.abs(py - (boardBottom + CONVEYOR_PADDING));

    const minDist = Math.min(distLeft, distRight, distTop, distBottom);

    if (minDist === distLeft || minDist === distRight) {
      const row = Math.floor((py - boardTop) / cellSize);
      if (row >= 0 && row < rows) {
        return { direction: minDist === distLeft ? 'right' : 'left', row };
      }
    } else {
      const col = Math.floor((px - boardLeft) / cellSize);
      if (col >= 0 && col < cols) {
        return { direction: minDist === distTop ? 'down' : 'up', col };
      }
    }

    return null;
  }

  /**
   * Collect all unique firing lines along the conveyor path from tStart to tEnd.
   * Samples at intervals small enough (half a cell width) to never skip a row/column.
   * Returns a de-duplicated array of FiringLines in traversal order.
   */
  private getSweptFiringLines(tStart: number, tEnd: number): FiringLine[] {
    // Sample interval: half a cell in t-space ensures we never skip a cell
    const halfCellT = (this.boardGeometry.cellSize * 0.5) / this.conveyor.getTotalLength();
    const dt = tEnd - tStart;
    if (dt <= 0) return [];

    const sampleCount = Math.max(1, Math.ceil(dt / halfCellT));
    const seen = new Set<number>();
    const lines: FiringLine[] = [];

    for (let i = 0; i <= sampleCount; i++) {
      const sampleT = tStart + (dt * i) / sampleCount;
      const pos = this.conveyor.getPositionAtT(sampleT);
      const fl = this.getFiringLine(pos.x, pos.y);
      if (!fl) continue;

      const lineKey = firingLineKey(fl.direction, fl.row ?? fl.col!);
      if (seen.has(lineKey)) continue;
      seen.add(lineKey);
      lines.push(fl);
    }

    return lines;
  }

  /**
   * Play the shots the model already resolved, as the belt reaches their lines.
   *
   * Decides NOTHING. Each pending lap carries an ordered shot list produced by
   * the shared simulator when the dispatch resolved; the conveyor path visits
   * firing lines in exactly the perimeter order that simulator used (bottom
   * left→right, right bottom→top, top right→left, left top→bottom), so the list
   * is consumed strictly in order. A swept line that isn't the next shot's line
   * is simply a line this attacker had nothing to fire at.
   *
   * A shot the sweep somehow misses is not lost: {@link finishRider} flushes
   * whatever is left when the lap ends, so the rendered board converges on the
   * model's board at least once per lap.
   */
  private advanceLapPresentation(): void {
    // Snapshot: finishing a rider splices the conveyor's internal array.
    const positions = Array.from(this.conveyor.getAttackerPositions());

    for (const entry of positions) {
      const pending = this.pendingLaps.get(entry.attackerId);
      if (!pending) continue;
      if (this.attackerRenderer.transitioningAttackers.has(entry.attackerId)) continue;
      if (pending.next >= pending.shots.length) continue;

      for (const firingLine of this.getSweptFiringLines(entry.prevT, entry.t)) {
        const shot = pending.shots[pending.next];
        if (!shot) break;
        if (shot.lineKey !== firingLineKey(firingLine.direction, firingLine.row ?? firingLine.col!)) continue;
        this.playResolvedShot(pending, shot);
        pending.next++;
      }

      // Spent attackers leave the belt the moment their last shot lands, which
      // is what the model did the moment the dispatch resolved.
      if (pending.next >= pending.shots.length && pending.outcome.depleted) {
        this.finishRider(entry.attackerId);
        if (this.gameEnded) return;
      }
    }
  }

  /**
   * Apply one resolved shot to the PRESENTATION: tick the sprite's ammo label,
   * clear the exact cell the model already killed, and launch the projectile.
   *
   * The target is named, never searched for. Re-deriving it from the rendered
   * board would let a later attacker steal a cube an earlier dispatch already
   * claimed, which is the whole class of bug this design removes.
   */
  private playResolvedShot(pending: PendingLap, shot: LapShotRecord): void {
    const attacker = pending.attacker;
    const orb = attacker.orbs[shot.orbIndex];
    if (orb) {
      orb.ammo = Math.max(0, orb.ammo - 1);
      if (orb.mystery) {
        orb.mystery = false;
        this.queueRenderer.revealMysteryOrb(attacker);
      }
    }

    const event = this.applyResolvedShotToBoard(attacker, shot);
    if (event) this.animateShot(attacker.id, event);

    if (attackerExhausted(attacker)) attacker.state = 'exhausted';
    this.attackerRenderer.updateAttackerAmmoLabel(attacker.id, attacker.orbs);
    this.winCheckDirty = true; this.reachHUD?.markDirty();
  }

  /**
   * Mirror one resolved shot onto `BoardSystem`, returning the {@link ShotEvent}
   * `animateShot` needs.
   *
   * Door HP follows the model exactly: doors lose a point per CLEARED CELL of
   * their colour, so a mega hit that doesn't destroy costs a door nothing and a
   * mega that shatters costs it one per footprint cell. (`ShootingSystem` used
   * to charge one per shot regardless, which the model never agreed with.)
   *
   * Mega and wall HP are applied as decrements rather than as the model's
   * recorded absolute value, so overlapping riders can't make an HP counter jump
   * backwards on screen. The totals are identical either way.
   */
  private applyResolvedShotToBoard(attacker: AttackerData, shot: LapShotRecord): ShotEvent | null {
    const base = {
      attackerId: attacker.id,
      orbIndex: shot.orbIndex,
      target: { row: shot.target.row, col: shot.target.col },
      colorIndex: shot.colorIndex,
    };

    if (shot.kind === 'wall' && shot.wallId !== undefined) {
      const hit = this.board.hitBlockadeWall(shot.wallId);
      const wall = this.board.getBlockadeWall(shot.wallId);
      if (!hit) return null;
      return {
        ...base,
        blockadeWallHit: {
          wallId: shot.wallId,
          remainingHp: hit.remainingHp,
          destroyed: hit.destroyed,
          wall: wall ?? {
            id: shot.wallId, side: 'left', colorIndex: shot.colorIndex,
            hp: 0, maxHp: 0, startCell: 0, length: 0,
          },
        },
      };
    }

    if (shot.kind === 'mega' && shot.megaId !== undefined) {
      const mb = this.board.hitMegaBlock(shot.megaId);
      if (!mb) return null;
      const unlocked = this.hitDoorsPerClearedCell(shot.colorIndex, shot.megaClearedCells?.length ?? 0);
      return {
        ...base,
        megaBlockId: shot.megaId,
        megaBlockHpRemaining: Math.max(0, mb.hp),
        unlockedDoors: unlocked.length > 0 ? unlocked : undefined,
      };
    }

    const removal = shot.removal ?? shot.target;
    if (this.board.removeTopCube(removal.row, removal.col) === null) {
      console.error(
        `[ordered-flow] resolved shot wanted cell (${removal.row},${removal.col}) ` +
        `for #${attacker.id} but the rendered board had nothing there`,
      );
      return null;
    }
    const unlocked = this.hitDoorsPerClearedCell(shot.colorIndex, 1);
    return {
      ...base,
      snakeRemovalTarget: shot.kind === 'snake' ? { row: removal.row, col: removal.col } : undefined,
      unlockedDoors: unlocked.length > 0 ? unlocked : undefined,
    };
  }

  /** Charge locked doors one HP per cleared cell of that colour, per the model. */
  private hitDoorsPerClearedCell(colorIndex: number, clearedCells: number): LockedDoor[] {
    const unlocked: LockedDoor[] = [];
    for (let i = 0; i < clearedCells; i++) {
      unlocked.push(...this.board.hitDoor(colorIndex));
    }
    return unlocked;
  }


  private animateShot(attackerId: number, shot: ShotEvent): void {
    const sprite = this.attackerRenderer.attackerSprites.get(attackerId);
    if (!sprite) return;

    const { offsetX, offsetY, cellSize } = this.boardGeometry;
    const targetX = offsetX + shot.target.col * cellSize + cellSize / 2;
    const targetY = offsetY + shot.target.row * cellSize + cellSize / 2;

    // After the projectile arrives: update locked door visuals if any were affected
    const handleDoorEffects = () => {
      // Update HP displays for all active doors matching this color
      for (const [, door] of this.board.getActiveDoors()) {
        if (door.colorIndex === shot.colorIndex) {
          this.boardRenderer.handleLockedDoorHpUpdate(door.id, door.hp, door.maxHp);
        }
      }
      // Animate unlocked doors
      if (shot.unlockedDoors) {
        for (const door of shot.unlockedDoors) {
          this.boardRenderer.handleLockedDoorUnlock(door);
        }
      }
    };

    if (shot.blockadeWallHit) {
      // Blockade wall hit — animate projectile to the wall, then update HP
      const wallEntry = this.boardRenderer.blockadeWallSprites.get(shot.blockadeWallHit.wallId);
      const wallTargetX = wallEntry ? wallEntry.container.x : targetX;
      const wallTargetY = wallEntry ? wallEntry.container.y : targetY;
      const wallHit = shot.blockadeWallHit;
      new ProjectileSprite(this, sprite.x, sprite.y, wallTargetX, wallTargetY, shot.colorIndex, () => {
        if (wallHit.destroyed) {
          this.boardRenderer.handleBlockadeWallDestroy(wallHit.wallId, shot.colorIndex);
        } else {
          this.boardRenderer.handleBlockadeWallHpUpdate(wallHit.wallId, wallHit.remainingHp, wallHit.wall.maxHp);
        }
      });
    } else if (shot.megaBlockId !== undefined) {
      // Mega block hit — animate projectile to the mega block center, then update HP
      const mbSprite = this.boardRenderer.megaBlockSprites.get(shot.megaBlockId);
      const megaTargetX = mbSprite ? mbSprite.container.x : targetX;
      const megaTargetY = mbSprite ? mbSprite.container.y : targetY;
      new ProjectileSprite(this, sprite.x, sprite.y, megaTargetX, megaTargetY, shot.colorIndex, () => {
        this.boardRenderer.handleMegaBlockHit(shot.megaBlockId!, shot.megaBlockHpRemaining!);
        handleDoorEffects();
      });
    } else {
      // For snake blocks, the cell removed is the bottom of the snake, not the target
      const removalRow = shot.snakeRemovalTarget ? shot.snakeRemovalTarget.row : shot.target.row;
      const removalCol = shot.snakeRemovalTarget ? shot.snakeRemovalTarget.col : shot.target.col;

      new ProjectileSprite(this, sprite.x, sprite.y, targetX, targetY, shot.colorIndex, () => {
        this.boardRenderer.handleCubeHit(removalRow, removalCol);
        handleDoorEffects();
      });
    }
  }








  // ── Win-state speed-up ───────────────────────────────────────────────────

  /**
   * Read off the MODEL, not the screen. The rendered board lags the logical one
   * by however much of a lap animation is still in flight, and the whole point
   * of this check is a claim about what must happen next.
   */
  private checkGuaranteedWin(): void {
    if (!this.winCheckDirty) return;
    this.winCheckDirty = false;

    const state = this.flow.state;

    // Queue and waiting slots must be empty — no further dispatch can happen.
    for (const lane of state.queueLanes) if (lane.length > 0) return;
    if (state.stack.length > 0) return;
    // Something must still be riding.
    if (state.conveyor.length === 0) return;

    // Count cubes remaining per color
    const cubesPerColor = new Map<number, number>();
    for (let i = 0; i < state.board.length; i++) {
      const v = state.board[i];
      if (v !== 0) cubesPerColor.set(v - 1, (cubesPerColor.get(v - 1) ?? 0) + 1);
    }

    // No cubes? Win will be detected by the normal check
    if (cubesPerColor.size === 0) return;

    // Count ammo per color among the riders
    const ammoPerColor = new Map<number, number>();
    for (const a of state.conveyor) {
      for (const orb of a.orbs) {
        ammoPerColor.set(orb.color, (ammoPerColor.get(orb.color) ?? 0) + orb.ammo);
      }
    }

    // Every cube color must be covered by sufficient ammo
    for (const [color, cubeCount] of cubesPerColor) {
      if ((ammoPerColor.get(color) ?? 0) < cubeCount) return;
    }

    // Guaranteed win — activate speed-up
    this.winSpeedUpActive = true;
    setReducedParticles(true);
    soundManager.speedUp();

    // Ramp conveyor speed to 3x over 500ms
    this.tweens.add({
      targets: this,
      conveyorSpeedMultiplier: 3,
      duration: 500,
      ease: 'Quad.easeIn',
    });

    // Visual cue: glowing conveyor overlay
    this.conveyorRenderer.addConveyorSpeedGlow();
  }

  /**
   * Checks if the game is in a deadlock state — no possible action can lead to
   * any cube being cleared or any frozen attacker being thawed.
   *
   * CONSERVATIVE: Only returns true when absolutely certain no progress is possible.
   * It is far worse to false-positive (end a winnable game) than to miss a deadlock.
   */
  /**
   * Delegates to the pure isDeadlocked() function for testability.
   */
  private checkForDeadlock(): boolean {
    return isDeadlocked(
      this.conveyor.attackerCount(),
      this.attackerRenderer.transitioningAttackers.size,
      this.queue,
      this.board,
      this.stack,
    );
  }

  /**
   * Handle the "stuck on frozen front" state: conveyor + stack empty, every
   * queue front is frozen. The runtime force-dispatches the frozen attacker
   * at the front of the lowest-numbered lane, mirroring the
   * StrategicSimulator + PerfectSolver force-dispatch fallback. Shows a brief
   * "Forced!" indicator so the player understands what happened.
   *
   * Returns true if a force-dispatch was performed (caller should early-return
   * for this frame) or if the state is genuinely deadlocked (Game Over was
   * triggered). Returns false if no action was taken.
   */
  private handleStuckOnFrozenFront(): boolean {
    const info = findStuckOnFrozenFrontLane(
      this.conveyor.attackerCount(),
      this.attackerRenderer.transitioningAttackers.size,
      this.queue,
      this.stack,
    );
    if (!info) {
      // Not stuck — clear the guard so future force-dispatches on a new
      // attacker aren't mistakenly flagged as a loop.
      this.lastForcedDispatchAttackerId = null;
      return false;
    }

    // Loop-safety: if we just force-dispatched this same attacker last frame
    // and somehow we're still stuck on it, declare a real deadlock. In
    // practice this shouldn't happen because force-dispatch always moves the
    // attacker onto the conveyor (breaking the stuck condition), but be
    // defensive.
    if (this.lastForcedDispatchAttackerId === info.attackerId) {
      console.warn(
        `[GameScene] Force-dispatch loop guard triggered on attacker ${info.attackerId} — declaring deadlock.`,
      );
      this.triggerDeadlockGameOver();
      return true;
    }

    // Same resolve-at-dispatch path as every other driver — the model has its
    // own stuck-on-frozen-front escape hatch and will accept this one.
    const result = this.dispatchFromQueueLane(info.laneIndex, 'AUTO', undefined, true);
    if (!result) {
      // Shouldn't happen (we just confirmed a frozen front exists), but be
      // safe and bail so we don't thrash on this frame.
      return false;
    }

    this.lastForcedDispatchAttackerId = result.attacker.id;
    this.queueRenderer.showForcedDispatch(info.laneIndex, this.boardGeometry);
    this.invalidateAutoplaySolution();
    return true;
  }

  /** Trigger game over due to deadlock. */
  private triggerDeadlockGameOver(): void {
    if (this.gameEnded) return;
    this.gameEnded = true;
    const traceText = this.extractTraceText();
    soundManager.levelLose();
    this.time.delayedCall(500, () => {
      this.scene.pause();
      this.scene.launch('GameOverScene', {
        won: false, level: this.level, reason: 'deadlock',
        decisionLog: this.decisionLog.length > 0 ? [...this.decisionLog] : undefined,
        levelName: this.levelName,
        traceText,
        queueOverride: this.queueOverride,
      });
    });
  }

  /**
   * Check for soft deadlock using reachability analysis.
   * Only runs if the cooldown has elapsed (debounced to ~1s).
   */
  private checkForSoftDeadlock(): boolean {
    if (this.gameEnded) return false;
    if (this.board.isCleared()) return false;

    return this.softDeadlockChecker.check(
      this.board,
      this.activeAttackers.values(),
      this.stack,
      this.queue,
    );
  }

  /** Trigger game over due to soft deadlock (mathematically unwinnable). */
  private triggerSoftDeadlockGameOver(): void {
    if (this.gameEnded) return;
    console.log(`[SoftDeadlock] Level ${this.level}: no progress possible — unwinnable state detected`);
    this.gameEnded = true;
    const traceText = this.extractTraceText();
    soundManager.levelLose();
    // Brief delay so the loss doesn't feel jarring
    this.time.delayedCall(800, () => {
      this.scene.pause();
      this.scene.launch('GameOverScene', {
        won: false, level: this.level, reason: 'soft_deadlock',
        decisionLog: this.decisionLog.length > 0 ? [...this.decisionLog] : undefined,
        levelName: this.levelName,
        traceText,
        queueOverride: this.queueOverride,
      });
    });
  }

  /**
   * During win-state speed-up, auto-dispatch everything still waiting.
   *
   * Goes through the same resolve-at-dispatch path as a tap — the speed-up is a
   * pacing effect, never a second set of rules. Stops at the first refusal so a
   * belt that cannot make room this frame is simply retried next frame.
   */
  private autoDispatch(): void {
    const slots = this.stack.getSlots();
    for (let i = 0; i < slots.length; i++) {
      if (!slots[i]) continue;
      if (!this.conveyor.canAdd(attackerSize(slots[i]!))) break;
      if (!this.dispatchFromStackSlot(i, 'AUTO')) break;
    }

    const nq = this.queue.numLanes();
    const dispatched = new Set<number>();
    for (let lane = 0; lane < nq; lane++) {
      const peeked = this.queue.peekQueue(lane);
      if (!peeked || dispatched.has(peeked.id)) continue;
      if (!this.conveyor.canAdd(attackerSize(peeked))) break;
      const result = this.dispatchFromQueueLane(lane, 'AUTO');
      if (!result) break;
      dispatched.add(result.attacker.id);
    }
  }



  // ── Autoplay ──────────────────────────────────────────────────────────────

  private createAutoplayToggle(): void {
    const x = 32;
    const y = 104;
    const autoW = 80;
    const h = 44;
    const r = 12;
    const gap = 6;
    const speedW = 64;

    // ── Autoplay on/off toggle ──
    const autoContainer = this.add.container(x, y).setDepth(100);

    const autoBg = this.add.graphics();
    this.drawToggleBg(autoBg, autoW, h, r, false);
    autoContainer.add(autoBg);

    const autoLabel = crispText(this.add.text(autoW / 2, h / 2 + 2, 'Auto', {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: '#8888aa',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);
    autoContainer.add(autoLabel);

    const autoHit = this.add.rectangle(autoW / 2, h / 2, autoW, h, 0x000000, 0);
    autoHit.setInteractive({ useHandCursor: true });
    autoContainer.add(autoHit);

    autoHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      if (this.autoplayEnabled) this.disableAutoplay();
      else this.enableAutoplay();
    });

    this.autoplayToggleBtn = autoContainer;
    this.autoplayToggleBg = autoBg;
    this.autoplayToggleLabel = autoLabel;

    // ── Speed cycle button ──
    const speedX = x + autoW + gap;
    const speedContainer = this.add.container(speedX, y).setDepth(100);

    const speedBg = this.add.graphics();
    this.drawSpeedBg(speedBg, speedW, h, r);
    speedContainer.add(speedBg);

    const speedLabel = crispText(this.add.text(speedW / 2, h / 2 + 2, '1x', {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: '#8888aa',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);
    speedContainer.add(speedLabel);

    const speedHit = this.add.rectangle(speedW / 2, h / 2, speedW, h, 0x000000, 0);
    speedHit.setInteractive({ useHandCursor: true });
    speedContainer.add(speedHit);

    speedHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.setSpeedStep((this.speedStepIndex + 1) % GameScene.SPEED_STEPS.length, fxDisabled);
    });

    this.speedBtn = speedContainer;
    this.speedBtnBg = speedBg;
    this.speedBtnLabel = speedLabel;

    // Keyboard shortcuts: 1/2/3 → speeds 1x/4x/10x
    const onKey = (step: number) => () => this.setSpeedStep(step, fxDisabled);
    this.input.keyboard?.on('keydown-ONE', onKey(0));
    this.input.keyboard?.on('keydown-TWO', onKey(1));
    this.input.keyboard?.on('keydown-THREE', onKey(2));
  }

  /**
   * Dev/automation entry point: `?autoplay=1` starts the level self-playing and
   * `&speed=N` presets the speed (N one of {@link GameScene.SPEED_STEPS}), so
   * `/?level=3&autoplay=1&speed=4` launches straight into a self-playing level.
   * `?replay=witness` is the explicit witness-only form — see
   * {@link startWitnessReplay}.
   *
   * BootScene already routes `?level=N|uuid`; this is the other half, and exists
   * because CDP-driven clicks and keypresses don't reach Phaser when the browser
   * window lacks OS focus — an unattended gate run can't press the Auto button.
   *
   * A no-op when the params are absent.
   */
  private applyAutoplayUrlParams(): void {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);

    const speedParam = params.get('speed');
    if (speedParam !== null) {
      const step = GameScene.SPEED_STEPS.indexOf(Number(speedParam) as typeof GameScene.SPEED_STEPS[number]);
      if (step >= 0) this.setSpeedStep(step, fxDisabled);
    }

    if (params.get('replay') === 'witness') {
      this.startWitnessReplay('explicit');
      return;
    }
    if (params.get('autoplay') !== '1') return;
    // Exactly what the Auto toggle's pointerdown handler does.
    this.enableAutoplay();
  }

  /**
   * Turn Auto on. On a level that ships a `gen.proof` and hasn't been played yet
   * this replays the PROVEN line rather than the strategic heuristic — a bot
   * loss on a proven level is then unambiguous. Everywhere else (no proof,
   * mid-game, witness unavailable) the heuristic bot runs exactly as before.
   */
  private enableAutoplay(): void {
    this.autoplayEnabled = true;
    this.autoplayTimer = 0;
    if (!this.startWitnessReplay('autoplay')) {
      this.autoplaySystem?.requestFullSolution();
    }
    this.updateAutoplayToggleVisual();
  }

  /** Turn Auto off, stopping whichever driver was running. */
  private disableAutoplay(): void {
    this.autoplayEnabled = false;
    this.autoplayTimer = 0;
    if (this.witnessMode === 'autoplay') this.stopWitnessReplay();
    this.updateAutoplayToggleVisual();
  }

  private setSpeedStep(step: number, fxDisabled: boolean): void {
    this.speedStepIndex = step;
    const speed = GameScene.SPEED_STEPS[this.speedStepIndex];
    this.autoplaySpeedMultiplier = speed;
    if (!fxDisabled) {
      this.time.timeScale = speed;
      this.tweens.timeScale = speed;
    }
    this.updateSpeedButtonVisual();
  }

  private drawToggleBg(g: Phaser.GameObjects.Graphics, w: number, h: number, r: number, active: boolean): void {
    g.clear();
    if (active) {
      g.fillStyle(0x44aa66, 0.85);
      g.lineStyle(2, 0x66dd88, 0.9);
    } else {
      g.fillStyle(0x1a1a2e, 0.7);
      g.lineStyle(2, 0x3a3a5a, 0.5);
    }
    g.fillRoundedRect(0, 0, w, h, r);
    g.strokeRoundedRect(0, 0, w, h, r);
  }

  private updateAutoplayToggleVisual(): void {
    if (!this.autoplayToggleBg || !this.autoplayToggleLabel) return;
    // "Proof" (mint) instead of "Auto" (white) says the proven line is driving,
    // not the heuristic — the one thing worth telling apart at a glance.
    const provenLine = this.witnessActive();
    this.drawToggleBg(this.autoplayToggleBg, 80, 44, 12, this.autoplayEnabled || provenLine);
    this.autoplayToggleLabel.setColor(
      provenLine ? '#88ffcc' : this.autoplayEnabled ? '#ffffff' : '#8888aa',
    );
    this.autoplayToggleLabel.setText(provenLine ? 'Proof' : 'Auto');
  }

  private drawSpeedBg(g: Phaser.GameObjects.Graphics, w: number, h: number, r: number): void {
    g.clear();
    const isAccelerated = this.speedStepIndex > 0;
    if (isAccelerated) {
      g.fillStyle(0x4466aa, 0.85);
      g.lineStyle(2, 0x6688dd, 0.9);
    } else {
      g.fillStyle(0x1a1a2e, 0.7);
      g.lineStyle(2, 0x3a3a5a, 0.5);
    }
    g.fillRoundedRect(0, 0, w, h, r);
    g.strokeRoundedRect(0, 0, w, h, r);
  }

  private updateSpeedButtonVisual(): void {
    if (!this.speedBtnBg || !this.speedBtnLabel) return;
    this.drawSpeedBg(this.speedBtnBg, 64, 44, 12);
    const speed = GameScene.SPEED_STEPS[this.speedStepIndex];
    const isAccelerated = this.speedStepIndex > 0;
    this.speedBtnLabel.setColor(isAccelerated ? '#ffffff' : '#8888aa');
    this.speedBtnLabel.setText(`${speed}x`);
  }

  /** Download Log button — downloads the decision log (player + autoplay dispatches). */
  private createLogButton(): void {
    const logW = 140;
    const h = 32;
    const r = 10;
    const x = (GAME_WIDTH - logW) / 2;
    const y = 8;
    const logContainer = this.add.container(x, y).setDepth(100);

    const logBg = this.add.graphics();
    logBg.fillStyle(0x669933, 0.85);
    logBg.fillRoundedRect(0, 0, logW, h, r);

    const logLabel = crispText(this.add.text(logW / 2, h / 2 + 1, 'Download Log', {
      fontFamily: FONT_FAMILY,
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);

    const logHit = this.add.rectangle(logW / 2, h / 2, logW, h, 0x000000, 0);
    logHit.setInteractive({ useHandCursor: true });

    logContainer.add([logBg, logLabel, logHit]);

    logHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.downloadDecisionLog();
    });

    this.logBtn = logContainer;
  }

  private createFxToggle(): void {
    // Position below the TRC/DL/LOG row
    const x = 32;
    const y = 194; // below LOG row (152 + 36 + 6)
    const w = 56;
    const h = 36;
    const r = 10;

    const container = this.add.container(x, y).setDepth(100);

    const bg = this.add.graphics();
    // FX starts ON (not disabled), so draw as inactive (default state)
    this.drawToggleBg(bg, w, h, r, false);
    container.add(bg);

    const label = crispText(this.add.text(w / 2, h / 2 + 1, 'FX', {
      fontFamily: FONT_FAMILY,
      fontSize: '18px',
      color: '#8888aa',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);
    container.add(label);

    const hitArea = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      const nowDisabled = !fxDisabled;
      setFxDisabled(nowDisabled);
      if (nowDisabled) {
        // Disable FX: skip particles, make tweens effectively instant
        setReducedParticles(true);
        this.tweens.timeScale = 9999;
        this.time.timeScale = this.autoplaySpeedMultiplier * 9999;
      } else {
        // Re-enable FX: restore normal particle and tween behavior
        if (!this.winSpeedUpActive) setReducedParticles(false);
        const speed = GameScene.SPEED_STEPS[this.speedStepIndex];
        this.tweens.timeScale = speed;
        this.time.timeScale = speed;
      }
      this.updateFxToggleVisual();
    });

    this.fxToggleBtn = container;
    this.fxToggleBg = bg;
    this.fxToggleLabel = label;
  }

  private updateFxToggleVisual(): void {
    if (!this.fxToggleBg || !this.fxToggleLabel) return;
    // Active (highlighted) when FX is disabled (toggle is "on" = FX off)
    const active = fxDisabled;
    this.drawToggleBg(this.fxToggleBg, 56, 36, 10, active);
    this.fxToggleLabel.setColor(active ? '#ffffff' : '#8888aa');
    this.fxToggleLabel.setText(active ? 'noFX' : 'FX');
  }

  /** Create a tappable queue-lane-count selector (cycles 3→4→5→6→3). */
  private createQueueSelector(): void {
    const x = 32;
    const y = 236; // below FX toggle (194 + 36 + 6)
    const w = 56;
    const h = 36;
    const r = 10;

    const numQueues = this.queueOverride ?? this.currentImageLevel?.numQueues ?? DEFAULT_NUM_QUEUES;

    const container = this.add.container(x, y).setDepth(100);

    const bg = this.add.graphics();
    // Always show as "active" (highlighted) since it displays a value
    this.drawToggleBg(bg, w, h, r, true);
    container.add(bg);

    const label = crispText(this.add.text(w / 2, h / 2 + 1, `Q:${numQueues}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);
    container.add(label);

    const hitArea = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      // Cycle: 3 → 4 → 5 → 6 → 3
      const current = this.queueOverride ?? this.currentImageLevel?.numQueues ?? DEFAULT_NUM_QUEUES;
      const next = current >= 6 ? 3 : current + 1;
      this.queueOverride = next;
      // Restart with the new queue count. levelData must be passed through, or
      // init() sees no preloaded level, falls back to getDifficultyForLevel() and
      // LevelGenerator.generate(), and swaps the board for a random one — losing
      // the pixel art, the baked difficulty (hence multi-orb attackers appearing
      // from the tier table) and every special-item field. Changing queue count
      // should change lane packing and nothing else.
      launchGameScene(this, { level: this.level, queueOverride: next, levelData: this.currentImageLevel });
    });

    this.queueSelectorBtn = container;
    this.queueSelectorBg = bg;
    this.queueSelectorLabel = label;
  }

  /** Create a debug slider to blend between gameplay colors (0%) and visual hex colors (100%). */
  private createBlendSlider(): void {
    if (!this.boardRenderer.hasVisualPalette()) return;

    const x = (960 - 160) / 2; // horizontally centered (GAME_WIDTH=960, trackW=160)
    const y = 196; // below title bar, above board
    const trackW = 160;
    const trackH = 6;
    const thumbR = 12;

    const container = this.add.container(x, y).setDepth(100);

    // Label (centered above track)
    const label = crispText(this.add.text(trackW / 2, -16, 'Art Colors', {
      fontFamily: FONT_FAMILY,
      fontSize: '14px',
      color: '#8888aa',
      fontStyle: 'bold',
    }), false).setOrigin(0.5, 0.5);
    container.add(label);

    // Track background
    const track = this.add.graphics();
    track.fillStyle(0x3a3a5a, 0.8);
    track.fillRoundedRect(0, 0, trackW, trackH, 3);
    container.add(track);

    // Endpoint labels
    const leftLabel = crispText(this.add.text(0, trackH + 10, 'Game', {
      fontFamily: FONT_FAMILY,
      fontSize: '10px',
      color: '#666688',
    }), false).setOrigin(0, 0);
    container.add(leftLabel);

    const rightLabel = crispText(this.add.text(trackW, trackH + 10, 'Art', {
      fontFamily: FONT_FAMILY,
      fontSize: '10px',
      color: '#666688',
    }), false).setOrigin(1, 0);
    container.add(rightLabel);

    // Filled portion of track
    const fill = this.add.graphics();
    container.add(fill);

    // Thumb
    const thumb = this.add.circle(trackW, trackH / 2, thumbR, 0x66dd88, 1);
    thumb.setStrokeStyle(2, 0xffffff, 0.8);
    container.add(thumb);

    // Hit area covering the full slider region for easier interaction
    const hitArea = this.add.rectangle(trackW / 2, trackH / 2, trackW + thumbR * 2, thumbR * 2 + 10, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true, draggable: false });
    container.add(hitArea);

    const getBlendLabel = (pct: number): string => {
      if (pct === 0) return 'Game Colors';
      if (pct === 100) return 'Art Colors';
      return `Blend: ${pct}%`;
    };

    /** Update slider visuals and set blend factor (instant — just changes RT alpha). */
    const updateSlider = (factor: number) => {
      factor = Math.max(0, Math.min(1, factor));

      // Update thumb, fill bar, and label
      const thumbX = factor * trackW;
      thumb.setPosition(thumbX, trackH / 2);

      fill.clear();
      fill.fillStyle(0x66dd88, 0.6);
      fill.fillRoundedRect(0, 0, thumbX, trackH, 3);

      const pct = Math.round(factor * 100);
      label.setText(getBlendLabel(pct));

      // Instant: just sets the visual RT's alpha (no redraw)
      this.boardRenderer.setBlendFactor(factor);
    };

    // Pointer events for dragging
    let dragging = false;

    const getFactorFromPointer = (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - container.x;
      return Math.max(0, Math.min(1, localX / trackW));
    };

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      dragging = true;
      updateSlider(getFactorFromPointer(pointer));
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!dragging) return;
      updateSlider(getFactorFromPointer(pointer));
    });

    this.input.on('pointerup', () => {
      if (dragging) {
        dragging = false;
      }
    });

    // Initialize at 5%
    updateSlider(0.05);

    this.blendSliderContainer = container;
  }

  /**
   * Execute one autoplay decision tick. Called by the timer in update()
   * and once immediately when autoplay is enabled.
   */
  private executeAutoplayTick(): void {
    if (!this.autoplaySystem) return;
    if (this.gameEnded || this.gamePaused) return;

    // ── Replay-first pattern ──
    // 1. Check if we have a decision (from replay sequence or single worker)
    const decision = this.autoplaySystem.consumeDecision();
    if (!decision) {
      // 2. If worker is still solving, wait
      if (this.autoplaySystem.isSolving() || this.autoplaySystem.isWaiting()) {
        return;
      }
      // 3. Replay exhausted or no sequence — request a fresh full solve
      this.autoplaySystem.requestFullSolution();
      return;
    }

    const action = decision;
    console.log(`[Autoplay] conv=${this.conveyor.slotsUsed()} stack=${this.stack.count()} queue=${this.queue.queueLength()} cubes=${this.board.totalCubesRemaining()} → ${action.type}${action.type === 'dispatch_queue' ? ` lane=${action.lane}` : action.type === 'dispatch_stack' ? ` slot=${action.slotIndex}` : ''}`);

    if (action.type === 'dispatch_stack') {
      this.dispatchFromStackSlot(action.slotIndex, 'AUTO', action.scoring);
    } else if (action.type === 'dispatch_queue') {
      this.dispatchFromQueueLane(action.lane, 'AUTO', action.scoring);
    }
    // action.type === 'wait' -> do nothing
  }

  /**
   * Dispatch a waiting-slot attacker — the same path a tap on that slot takes.
   * Returns null when the model refuses the dispatch or the slot is empty.
   */
  private dispatchFromStackSlot(
    slotIndex: number,
    actor: DispatchActor,
    scoring?: import('../systems/AutoplaySystem').ScoringInfo,
  ): DispatchResult | null {
    const peeked = this.stack.getSlots()[slotIndex];
    if (!peeked) return null;

    const outcome = this.beginDispatch(peeked, 'stack', slotIndex);
    if (!outcome) return null;

    const slotPos = this.stackUI.getSlotPosition(slotIndex);
    const attacker = this.stack.dispatch(slotIndex);
    if (!attacker) {
      console.error(`[ordered-flow] the model dispatched #${peeked.id} from slot ${slotIndex} but the stack refused`);
      return null;
    }
    this.logDecision(actor, 'stack', slotIndex, attacker, scoring);
    this.completeDispatch(attacker, outcome, { x: slotPos.x, y: slotPos.y, scale: 0.6 });
    return { attacker, outcome };
  }

  /**
   * Dispatch a queue lane's front attacker — the same path a tap on that lane
   * takes. `force` uses the stuck-on-frozen-front escape hatch (semantics doc
   * §1), which the model allows only when nothing else can move.
   */
  private dispatchFromQueueLane(
    lane: number,
    actor: DispatchActor,
    scoring?: import('../systems/AutoplaySystem').ScoringInfo,
    force = false,
  ): DispatchResult | null {
    const peeked = force ? this.queue.peekFrozenFront(lane) : this.queue.peekQueue(lane);
    if (!peeked) return null;

    const outcome = this.beginDispatch(peeked, 'queue', lane);
    if (!outcome) return null;

    const queueSnapshot = this.queueRenderer.snapshotQueuePositions();
    const attacker = force ? this.queue.forceDispatchFrom(lane) : this.queue.dequeueFrom(lane);
    if (!attacker) {
      console.error(`[ordered-flow] the model dispatched #${peeked.id} from lane ${lane} but the queue refused`);
      return null;
    }
    this.logDecision(actor, 'queue', lane, peeked, scoring);

    const spanLanes = attacker.queueLanes ?? [lane];
    const nq = this.queue.numLanes();
    const laneW = getQueueLaneWidth(nq);
    const lanesStartX = getLanesStartX(nq);
    let fromX: number;
    if (spanLanes.length > 1) {
      const firstLaneX = lanesStartX + spanLanes[0] * (laneW + QUEUE_LANE_GAP);
      const lastLaneX = lanesStartX + spanLanes[spanLanes.length - 1] * (laneW + QUEUE_LANE_GAP);
      fromX = (firstLaneX + lastLaneX + laneW) / 2;
    } else {
      fromX = lanesStartX + lane * (laneW + QUEUE_LANE_GAP) + laneW / 2;
    }
    const fromY = getQueueY(this.boardGeometry) + 28;
    let orbFromPositions: { x: number; y: number }[] | undefined;
    if (spanLanes.length > 1) {
      orbFromPositions = spanLanes.map(sl => ({
        x: lanesStartX + sl * (laneW + QUEUE_LANE_GAP) + laneW / 2,
        y: getQueueY(this.boardGeometry) + 22,
      }));
    }
    this.completeDispatch(attacker, outcome, { x: fromX, y: fromY, scale: 1.1 }, orbFromPositions);
    this.queueRenderer.animateQueueShift(queueSnapshot);
    return { attacker, outcome };
  }

  // ── Ordered flow: resolve, then present ──────────────────────────────────

  /**
   * Resolve a dispatch in the model.
   *
   * This is where the whole architecture lives. The model decides the attacker's
   * ENTIRE lap right now — every cube it kills, every point of ammo it spends,
   * whether it survives — and the belt animation that follows is a replay. A
   * cube is dead the instant this returns, so a later dispatch resolving against
   * the same model can never target it, even while its kill animation is still
   * in flight.
   *
   * Belt room IS a veto — the classic rule, restored. A tap while the belt
   * lacks room for the attacker is refused with the gentle conveyor flash (the
   * buzz comes from the tap handler); nobody is evicted mid-lap to make room.
   * The eviction/burst machinery (`makeBeltRoom`, fast-forward hops, the
   * "bumped someone off" flash) is deleted, not flagged off.
   *
   * Returns null when the model refuses. `no-belt-room` is the expected,
   * player-facing refusal; every other refusal is a genuine illegality and a
   * bug somewhere upstream, logged and never swallowed.
   */
  private beginDispatch(
    peeked: AttackerData,
    source: 'queue' | 'stack',
    where: number,
  ): DispatchOutcome | null {
    if (this.gameEnded) return null;

    const outcome = this.flow.dispatch(peeked.id);
    if (!outcome) {
      if (this.flow.lastRefusal === 'no-belt-room') {
        // The classic full-belt refusal cue (pre-consolidation behaviour).
        this.conveyorRenderer.flashConveyorFull();
        return null;
      }
      console.error(
        `[ordered-flow] model refused dispatch of #${peeked.id} from ${source} ${where}: ` +
        `${this.flow.lastRefusal} — the presentation offered a move the model does not have`,
      );
      return null;
    }
    // In live mode the model never force-arrives anyone (the belt is
    // real-time); a forced arrival here means the engine mode is wrong.
    if (outcome.forcedArrivals.length > 0) {
      console.error('[ordered-flow] live dispatch produced forced arrivals — engine is not in live mode');
    }
    // `outcome.lost` here can only mean the model hit a dead end after a
    // successful dispatch; update() reads `flow.lost` and ends the game, so the
    // dispatch still presents normally.
    return outcome;
  }

  /** Register the resolved lap and start the belt animation. */
  private completeDispatch(
    attacker: AttackerData,
    outcome: DispatchOutcome,
    fromPos: { x: number; y: number; scale?: number },
    orbFromPositions?: { x: number; y: number }[],
  ): void {
    // The lap emptied the attacker: every still-frozen queue attacker ticks
    // down. The model did this at resolve time (whatever the dispatch's source),
    // so the runtime queue does too — if the two disagreed about who is frozen,
    // a legal tap could be refused.
    if (outcome.depleted) {
      const thawed = this.queue.thawFrozen();
      if (thawed.length > 0) this.queueRenderer.handleFrozenThaw(thawed);
    }
    this.pendingLaps.set(attacker.id, { attacker, shots: outcome.shots, next: 0, outcome });
    this.winCheckDirty = true; this.reachHUD?.markDirty();
    soundManager.tap();
    this.launchAttackerOnConveyor(attacker, fromPos, orbFromPositions);
  }

  /**
   * A rider is done with the belt: its lap animation reached the end. Flush any
   * shots the sweep has not played yet (so the rendered board always catches up
   * with the model at lap end), take it off the belt, then either fade it
   * (spent) or arrive it on the stack.
   *
   * This is the ONLY way a rider leaves the belt in the live game — the burst
   * eviction (`makeBeltRoom` + mid-lap fast-forward) is deleted, per Arthur's
   * playtest ruling.
   */
  private finishRider(attackerId: number): void {
    const pending = this.pendingLaps.get(attackerId);
    if (pending) {
      while (pending.next < pending.shots.length) {
        this.playResolvedShot(pending, pending.shots[pending.next]);
        pending.next++;
      }
      this.pendingLaps.delete(attackerId);
    }
    this.conveyor.removeAttacker(attackerId);
    // Kills a multi-orb stagger mid-flight; the flag itself has to go too,
    // because a single-orb launch tween has no cleanup entry to find.
    this.attackerRenderer.cleanupTransitionAnimation(attackerId);
    this.attackerRenderer.transitioningAttackers.delete(attackerId);
    this.winCheckDirty = true; this.reachHUD?.markDirty();

    const attacker = this.activeAttackers.get(attackerId);
    const inFlight = this.flow.peekOldestInFlight();

    if (inFlight !== attackerId) {
      if (this.flow.find(attackerId)?.where === 'in-flight') {
        // The belt finished this rider's lap before the ring's older rider(s)
        // — presentation drift (e.g. entrance animations of different lengths
        // resuming out of dispatch order). The RING is the authority on
        // arrival order: leave the rider in the ring for reconcileFlowRing()
        // to settle the moment it reaches the front. It must NOT be faded
        // here — fading a ring rider leaks its belt slots forever and, once
        // one leaked rider heads the ring, turns every later survivor into
        // another leak (the fire-station cascade).
        console.error(
          `[ordered-flow] rider #${attackerId} finished its lap out of order — ` +
          `the ring's oldest is #${inFlight}; deferring its arrival to ring order`,
        );
        return;
      }
      // Not in the ring → the model already culled it when its ammo ran out.
      if (attacker) {
        this.attackerRenderer.fadeExhaustedAttackerSprite(attackerId, primaryColor(attacker));
        this.activeAttackers.delete(attackerId);
      }
      if (this.flow.won && this.allShotsPlayed()) this.triggerWin();
      return;
    }

    // The model's `settle`: the ring's oldest rider arrives on the stack.
    const arrival = this.flow.settle();
    if (!arrival) {
      console.error(`[ordered-flow] settle refused for #${attackerId}: ${this.flow.lastRefusal}`);
      return;
    }
    this.presentArrival(arrival);
  }

  /**
   * Show an arrival the model has already performed. An overflow arrival is the
   * loss: it is timed to the moment the sprite reaches the (over-full) waiting
   * area, so the player sees what killed them.
   */
  private presentArrival(arrival: ArrivalRecord): void {
    if (arrival.culled) {
      // The model culled this arrival instead of seating it — every orb was
      // empty (the engine already shouted). Exhausted riders never arrive
      // anywhere: present the cull as the usual exhaustion fade, never as a
      // zero-ammo attacker squatting in a waiting slot.
      const culledAttacker = this.activeAttackers.get(arrival.attackerId);
      this.pendingLaps.delete(arrival.attackerId);
      this.conveyor.removeAttacker(arrival.attackerId);
      this.activeAttackers.delete(arrival.attackerId);
      if (culledAttacker) {
        this.attackerRenderer.fadeExhaustedAttackerSprite(arrival.attackerId, primaryColor(culledAttacker));
      } else {
        const orphan = this.attackerRenderer.attackerSprites.get(arrival.attackerId);
        this.attackerRenderer.attackerSprites.delete(arrival.attackerId);
        orphan?.destroy();
      }
      return;
    }

    const attacker = this.activeAttackers.get(arrival.attackerId);
    const sprite = this.attackerRenderer.attackerSprites.get(arrival.attackerId);
    this.attackerRenderer.attackerSprites.delete(arrival.attackerId);
    this.pendingLaps.delete(arrival.attackerId);
    this.conveyor.removeAttacker(arrival.attackerId);
    this.activeAttackers.delete(arrival.attackerId);

    // The model seated this rider with ammo remaining. If the presentation's
    // copy drifted (a missed shot animation, a flushed-late lap), resync it to
    // the model — the stack UI must never show ammo the model does not have,
    // and above all never a spent attacker the model knows is live.
    if (attacker) {
      const seated = this.flow.find(arrival.attackerId);
      if (seated?.where === 'stack') {
        for (let i = 0; i < attacker.orbs.length; i++) {
          const modelOrb = seated.attacker.orbs[i];
          if (modelOrb && attacker.orbs[i].ammo !== modelOrb.ammo) {
            console.error(
              `[ordered-flow] runtime ammo for #${arrival.attackerId} orb ${i} ` +
              `(${attacker.orbs[i].ammo}) drifted from the model (${modelOrb.ammo}) — resyncing`,
            );
            attacker.orbs[i].ammo = modelOrb.ammo;
          }
        }
      }
    }

    if (arrival.overflow) {
      soundManager.levelLose();
      if (sprite) {
        this.tweens.add({
          targets: sprite, alpha: 0, scaleX: 0.5, scaleY: 0.5,
          duration: 250, ease: 'Power2',
          onComplete: () => sprite.destroy(),
        });
      }
      this.triggerOverflowGameOver();
      return;
    }

    if (attacker && !this.stack.addToStack(attacker)) {
      console.error(
        `[ordered-flow] the model seated #${arrival.attackerId} at stack slot ${arrival.stackSlot} ` +
        'but the runtime stack had no room',
      );
    }
    this.stackArrivals++;
    soundManager.lapComplete();

    if (this.stack.emptySlotCount() === 0) this.stackUI.flashFull();

    const slotIdx = attacker ? this.stack.getSlots().indexOf(attacker) : -1;
    if (slotIdx >= 0 && sprite) {
      const slotPos = this.stackUI.getSlotPosition(slotIdx);
      this.tweens.add({
        targets: sprite,
        x: slotPos.x,
        y: slotPos.y,
        scaleX: 0.6,
        scaleY: 0.6,
        rotation: 0,
        duration: 200,
        ease: 'Cubic.easeOut',
        onUpdate: () => counterRotateLabels(sprite, sprite.rotation),
        onComplete: () => sprite.destroy(),
      });
    } else if (sprite) {
      sprite.destroy();
    }
  }

  /**
   * Force-resume belt entries whose entrance animation stalled (killed tween,
   * occluded tab, dropped delayedCall). The entrance is display; after a grace
   * period well past the longest legitimate entrance (~0.7s for a 5-orb
   * stagger), the logic reclaims the rider so its lap still happens. Runs for
   * MANUAL play too — the old watchdog only ran under autoplay.
   */
  private unstickStalledEntrances(): void {
    for (const id of this.conveyor.stalledPausedIds(ENTRANCE_STALL_SECONDS)) {
      console.error(
        `[ordered-flow] rider #${id} stuck in its entrance animation for over ` +
        `${ENTRANCE_STALL_SECONDS}s — forcing it onto the belt (animation must never hold the logic hostage)`,
      );
      this.attackerRenderer.cleanupTransitionAnimation(id);
      this.attackerRenderer.transitioningAttackers.delete(id);
      this.conveyor.resumeAttacker(id);
    }
  }

  /**
   * INVARIANT (defense-in-depth): every ring rider must have a live belt entry.
   * The engine settles any front rider that lost its presentation — loudly,
   * with a state dump — and this presents those arrivals so the game continues
   * instead of soft-locking. Also flushes any resolved-but-never-animated shots
   * first so the rendered board converges with the model, and re-materialises a
   * runtime attacker from the model if the presentation lost it entirely.
   */
  private reconcileFlowRing(): void {
    if (this.gameEnded) return;
    if (this.flow.state.conveyor.length === 0) return;
    const present = new Set<number>();
    for (const entry of this.conveyor.getAttackerPositions()) present.add(entry.attackerId);
    const arrivals = this.flow.reconcileWithPresentation(present);
    for (const arrival of arrivals) {
      const pending = this.pendingLaps.get(arrival.attackerId);
      if (pending) {
        while (pending.next < pending.shots.length) {
          this.playResolvedShot(pending, pending.shots[pending.next]);
          pending.next++;
        }
      }
      this.ensureRuntimeAttacker(arrival.attackerId);
      this.presentArrival(arrival);
      if (this.gameEnded) return;
    }
  }

  /**
   * Re-materialise a runtime `AttackerData` from the model when the
   * presentation lost its copy (e.g. a launch that never animated). Without
   * this, a reconciled arrival would seat in the model's stack but show an
   * empty slot the player cannot tap.
   */
  private ensureRuntimeAttacker(attackerId: number): void {
    if (this.activeAttackers.has(attackerId)) return;
    const found = this.flow.find(attackerId);
    if (!found) return;
    console.error(
      `[ordered-flow] presentation lost attacker #${attackerId} — re-materialising it from the model`,
    );
    this.activeAttackers.set(attackerId, {
      id: attackerId,
      orbs: found.attacker.orbs.map(o => ({ colorIndex: o.color, ammo: o.ammo, maxAmmo: Math.max(o.ammo, 1) })),
      state: 'on_conveyor',
    });
  }

  /** The stack overflowed on an arrival — the one way ordered flow loses. */
  private triggerOverflowGameOver(): void {
    if (this.gameEnded) return;
    this.gameEnded = true;
    const traceText = this.extractTraceText();
    this.time.delayedCall(500, () => {
      this.scene.pause();
      this.scene.launch('GameOverScene', {
        won: false, level: this.level,
        decisionLog: this.decisionLog.length > 0 ? [...this.decisionLog] : undefined,
        levelName: this.levelName,
        traceText,
        queueOverride: this.queueOverride,
      });
    });
  }

  // ── Witness replay ────────────────────────────────────────────────────────

  /**
   * Start replaying this level's archived solver witness through the real
   * dispatch paths, proving (or disproving) offline/online parity end-to-end.
   * Honours `&speed=N`; `&stall=SEC` overrides the no-progress timeout.
   *
   * Serial mode (one attacker on the belt at a time) is the DEFAULT: the
   * solver proves a line by resolving one lap at a time, so that is the only
   * sequencing the proof actually claims anything about. `&serial=0` opts back
   * into concurrent riding (a strictly harder test — see docs/solver.md);
   * `&serial=1` is the explicit form of the default.
   *
   * A witness that still carries the retired `advance` action was proved under
   * the pre-2026-09-01 conveyor-parking model and is REFUSED — replaying it
   * would measure a model the game never implemented. Re-prove the level.
   *
   * Two entry points, differing only in how a missing witness is treated:
   *   `explicit`  — `?replay=witness`. The bot stays off and a missing or
   *                 broken witness is a loud FAIL; the whole point of the URL
   *                 is that only the witness drives the game.
   *   `autoplay`  — the Auto toggle / `?autoplay=1`. Used only on a level that
   *                 ships a `gen.proof` and hasn't been played yet (witness
   *                 lines are from-start only); anything else returns false and
   *                 the caller falls back to the strategic bot.
   *
   * Returns whether the witness took over.
   */
  private startWitnessReplay(mode: 'explicit' | 'autoplay'): boolean {
    if (typeof window === 'undefined') return false;
    const ref = this.currentImageLevel?.gen?.proof?.witnessRef;

    if (mode === 'autoplay') {
      if (!ref) return false; // unproven level — heuristic bot, as always
      if (!this.isAtLevelStart()) {
        console.log(`${REPLAY_LOG_PREFIX} level already in play — Auto falls back to the strategic bot`);
        return false;
      }
    } else {
      // The bot and the witness must never both dispatch.
      this.autoplayEnabled = false;
    }

    this.witnessMode = mode;
    this.witnessPending = true;
    this.witnessReported = false;
    this.ensureWitnessStatusText();
    this.setWitnessStatus('witness: loading…', '#aaaacc');
    this.updateAutoplayToggleVisual();

    if (!ref) {
      this.failWitnessSetup(`level ${this.level} (${this.levelName ?? 'unnamed'}) has no gen.proof.witnessRef`);
      return true;
    }

    const params = new URLSearchParams(window.location.search);
    const stallParam = Number(params.get('stall'));
    const serialParam = params.get('serial');
    const burstParam = params.get('burst');
    const options = {
      // Debug only. Ordered flow does not need one-rider-at-a-time; the implied
      // settle already reproduces a strict-model line under full concurrency.
      serial: serialParam === null ? undefined : serialParam !== '0',
      // Every archived witness today is strict-model, so the implied settle is
      // on by default. `&burst=1` turns it off for a native burst witness.
      impliedSettle: burstParam === null ? undefined : burstParam === '0',
    } as { serial?: boolean; impliedSettle?: boolean; stallMs?: number; pacing?: WitnessPacingOptions };
    if (Number.isFinite(stallParam) && stallParam > 0) options.stallMs = stallParam * 1000;

    // Captured synchronously: the schedule below is about the level-start
    // state, which is the only state a witness line is valid from anyway.
    const initialFlowState = this.flow.state;

    void fetchWitness(ref)
      .then(record => {
        if (!this.witnessPending) return; // cancelled (Auto toggled off, scene moved on)
        if (witnessHasAdvanceActions(record.witness)) {
          this.handleWitnessLoadFailure(`${ref}: ${LEGACY_WITNESS_MESSAGE}`);
          return;
        }
        const idMap = buildSolverIdMap(this.orderedAttackerIds, this.rawQueueLength, this.levelHammerIndices);
        // Native witnesses need lookahead pacing: the model's age bound lets a
        // trace put several actions between two back-to-back riders' arrivals,
        // and live that gap is real lap-end spacing the driver must create at
        // dispatch time (the lv2003 carnival-circus overflow, 2026-09).
        if (classifyWitness(record.witness) === 'native') {
          const schedule = computeWitnessArrivalSchedule(initialFlowState, record.witness, idMap);
          if (schedule) {
            options.pacing = { schedule };
          } else {
            console.warn(`${REPLAY_LOG_PREFIX} witness does not replay through the model verbatim — running unpaced`);
          }
        }
        this.witnessPending = false;
        const driver = new WitnessDriver(record.witness, idMap, options);
        this.witnessDriver = driver;
        console.log(
          `${REPLAY_LOG_PREFIX} loaded ${ref} — ${record.witness.length} actions, seed=${record.seed}, ` +
          `solverRev=${record.solverRev}, ${idMap.size} attackers mapped, ` +
          `${driver.serialMode ? 'serial' : 'concurrent'}` +
          `${driver.impliedSettleMode ? ' + implied settles (strict-model witness)' : ' + native settles'}` +
          `${options.pacing ? ` + pacing (${options.pacing.schedule.size} arrivals scheduled)` : ''}`,
        );
        this.updateWitnessStatus();
        this.updateAutoplayToggleVisual();
      })
      .catch((err: Error) => this.handleWitnessLoadFailure(err.message));
    return true;
  }

  /**
   * The witness couldn't be loaded. Under `?replay=witness` that's a hard FAIL;
   * under Auto it's a quiet hand-off to the strategic bot, which is what the
   * player asked for in the first place.
   */
  private handleWitnessLoadFailure(message: string): void {
    if (!this.witnessPending) return;
    if (this.witnessMode === 'explicit') {
      this.witnessPending = false;
      this.failWitnessSetup(message);
      return;
    }
    // Loud even though we recover: this level ships a witnessRef, so a failure
    // here means the archive or the generated queue moved out from under it.
    console.error(`${REPLAY_LOG_PREFIX} could not load the proven line (${message}) — Auto falls back to the strategic bot`);
    this.stopWitnessReplay();
    if (this.autoplayEnabled) this.autoplaySystem?.requestFullSolution();
  }

  /** The replay could not even start — say so loudly and leave the level playable by hand. */
  private failWitnessSetup(message: string): void {
    this.witnessReported = true;
    console.error(`${REPLAY_LOG_PREFIX} FAIL (setup): ${message}`);
    this.setWitnessStatus(`witness FAIL — ${message}`, '#ff6666');
    this.updateAutoplayToggleVisual();
  }

  /** Tear the replay down and clear its UI — used on hand-off and on Auto-off. */
  private stopWitnessReplay(): void {
    this.witnessPending = false;
    this.witnessMode = 'off';
    this.witnessDriver = undefined;
    this.witnessReported = false;
    this.witnessStatusText?.destroy();
    this.witnessStatusText = undefined;
  }

  /** True while a witness is loading or still driving the game. */
  private witnessActive(): boolean {
    return this.witnessPending || (this.witnessDriver !== undefined && !this.witnessReported);
  }

  /**
   * A witness line is only valid from the opening position: it names exact
   * attackers in exact places, so a single hand-played dispatch invalidates it.
   */
  private isAtLevelStart(): boolean {
    return this.decisionTick === 0
      && this.conveyor.attackerCount() === 0
      && this.stack.count() === 0;
  }

  private ensureWitnessStatusText(): void {
    if (this.witnessStatusText) return;
    this.witnessStatusText = crispText(this.add.text(GAME_WIDTH - 32, 96, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '20px',
      color: '#aaaacc',
    })).setOrigin(1, 0).setDepth(500);
  }

  /** Snapshot the live systems in the plain-data shape {@link WitnessDriver} reads. */
  private buildReplayView(): ReplayView {
    const laneHeads: (number | null)[] = [];
    const laneFronts: (number | null)[] = [];
    const queueIds = new Set<number>();
    for (let lane = 0; lane < this.queue.numLanes(); lane++) {
      const peeked = this.queue.peekQueue(lane);
      laneHeads.push(peeked ? peeked.id : null);
      const entries = this.queue.getQueueLane(lane);
      laneFronts.push(entries.length > 0 ? entries[0].id : null);
      for (const att of entries) queueIds.add(att.id);
    }

    return {
      laneHeads,
      laneFronts,
      queueIds,
      stackSlots: this.stack.getSlots().map(att => (att ? att.id : null)),
      // The MODEL's ring, not the sprites: an attacker that spent its last ammo
      // mid-lap is already gone logically while its fade still plays.
      inFlight: this.flow.inFlightIds(),
      // Belt room is a dispatch gate again (classic rule) — the driver waits
      // for room instead of tapping into a refusal. The model is the truth
      // about slot widths and occupancy.
      canAdd: (runtimeId: number): boolean => {
        const size = this.flow.find(runtimeId)?.attacker.slotCount ?? 1;
        return this.flow.inFlightSlotsUsed() + size <= this.flow.state.conveyorCap;
      },
      transitionsClear: this.attackerRenderer.transitioningAttackers.size === 0,
      gameTimeMs: this.witnessGameTimeMs,
      stackArrivals: this.stackArrivals,
      cubesRemaining: this.flow.cubesRemaining(),
      gameOver: this.gameEnded,
    };
  }

  /** One driver step: ask for the next input, perform it, record the outcome. */
  private tickWitnessReplay(nowMs: number): void {
    const driver = this.witnessDriver;
    if (!driver || driver.status !== 'running') return;

    const view = this.buildReplayView();
    const command = driver.next(view, nowMs);

    if (command.kind === 'dispatch-queue' || command.kind === 'dispatch-stack') {
      const result = command.kind === 'dispatch-queue'
        ? this.dispatchFromQueueLane(command.lane, 'REPLAY')
        : this.dispatchFromStackSlot(command.slotIndex, 'REPLAY');
      if (result && result.attacker.id === command.runtimeId) {
        // Tell the driver whether this dispatch produced a survivor. A
        // strict-model witness implies a `settle` right after one, and that is
        // how the driver honours it (see WitnessDriverOptions.impliedSettle).
        driver.onDispatched(result.outcome.inFlight ? result.attacker.id : undefined);
      } else {
        const where = command.kind === 'dispatch-queue' ? `lane ${command.lane}` : `slot ${command.slotIndex}`;
        driver.onDispatchRejected(view, result
          ? `${where} dispatched attacker #${result.attacker.id}, expected #${command.runtimeId}`
          : `${where} refused the dispatch`);
      }
    }

    this.updateWitnessStatus();
    if (driver.status !== 'running') this.reportWitnessReplay();
  }

  /** Emit the verdict exactly once — WIN to the log, divergence to the error channel. */
  private reportWitnessReplay(): void {
    const driver = this.witnessDriver;
    if (!driver || this.witnessReported) return;

    // Settle a still-running driver against the final state (a win or a loss
    // ends the scene before the driver's own tick sees it).
    if (driver.status === 'running') driver.next(this.buildReplayView(), this.time.now);
    if (driver.status === 'running') return;

    this.witnessReported = true;
    if (driver.status === 'won') {
      console.log(
        `${REPLAY_LOG_PREFIX} WIN in ${driver.index} steps (witness has ${driver.total}) — ` +
        'the runtime reproduced the proof',
      );
      this.setWitnessStatus(`witness ${driver.index}/${driver.total} WIN`, '#66dd88');
      return;
    }
    const failure = driver.failure;
    if (failure) {
      console.error(formatDivergence(failure));
      this.setWitnessStatus(`witness ${failure.step}/${driver.total} FAIL — ${failure.reason}`, '#ff6666');
      // STOP rather than hand back to the heuristic: a bot that quietly carries
      // on from here would mask the divergence we just found.
      this.autoplayEnabled = false;
      this.updateAutoplayToggleVisual();
    }
  }

  private updateWitnessStatus(): void {
    if (!this.witnessDriver || this.witnessReported) return;
    this.setWitnessStatus(this.witnessDriver.progressLabel(), '#aaaacc');
  }

  private setWitnessStatus(text: string, color: string): void {
    if (!this.witnessStatusText) return;
    this.witnessStatusText.setText(text);
    this.witnessStatusText.setColor(color);
  }

}
